/// <reference types="vite/client" />

import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

type Option = {
  id: string;
  label: string;
  detail: string;
  meta?: string;
  tone?: string;
};

type StoryState = {
  projectMode: string;
  sourceProject: string;
  inheritCharacters: boolean;
  inheritVisualDNA: boolean;
  inheritDirector: boolean;
  inheritAspect: boolean;
  inheritEvolink: boolean;
  plotFlex: number;
  seed: string;
  format: string;
  aspect: string;
  genre: string;
  tone: string;
  audience: string;
  structure: string;
  arc: string;
  stakes: string;
  twist: string;
  era: string;
  visual: string;
  director: string;
  characterDepth: number;
  castSize: number;
  leadType: string;
  supportType: string;
  antagonistType: string;
  characterLook: string;
  runtime: number;
  chunkProfile: string;
  imageModel: boolean;
  characterCards: boolean;
  faceLock: boolean;
  evolinkModel: string;
  videoResolution: string;
  autoUpscale: boolean;
  storyboards: boolean;
  voicePlan: boolean;
  audioMode: string;
  audioSfx: boolean;
  audioDialogue: boolean;
  audioVoiceover: boolean;
  audioScore: boolean;
  audioMusicBed: boolean;
  pitchDeck: boolean;
  renderPackage: boolean;
  spokenDialogue: boolean;
  scriptProvider: string;
  continuityChain: boolean;
  seedAudioVoices: boolean;
  subGenre: string;
};

type RenderGateKey = "scriptRights" | "likenessRights" | "voiceRights" | "brandIpRights" | "humanReview" | "finalConfirm";

type RenderGates = Record<RenderGateKey, boolean>;

type HandoffStatus = "idle" | "built" | "estimating" | "estimated" | "sending" | "sent" | "error";

type RenderHandoffState = {
  packageBuiltAt: string | null;
  estimateReviewedAt: string | null;
  estimateCredits: number | null;
  status: HandoffStatus;
  message: string;
  jobId: string;
};

type BlockingMark = {
  character: string;
  framePosition: string;
  depth: string;
  bodyOrientation: string;
  eyelineTarget: string;
  action: string;
};

type BlockingPlan = {
  characterMarks: BlockingMark[];
  objectMark: string;
  movementPath: string;
  cameraAxis: string;
  screenDirection: string;
  startFrame: string;
  endFrame: string;
  storyChange: string;
  continuityRisk: string;
};

type StoryLogicBeat = {
  label: string;
  time: string;
  purpose: string;
  threat: string;
  choice: string;
  visibleAction: string;
  consequence: string;
  locationBridge: string;
  payoff: string;
  promptRule: string;
  blockingPlan: BlockingPlan;
};

type StoryCoherenceRule = {
  label: string;
  contract: string;
  promptRule: string;
};

type SkillSource = {
  label: string;
  scope: string;
  rule: string;
  source: string;
};

type Blueprint = {
  title: string;
  logline: string;
  storyObject: string;
  acts: Array<{ label: string; time: string; text: string }>;
  logicBeats: StoryLogicBeat[];
  coherenceRules: StoryCoherenceRule[];
  skillSources: SkillSource[];
  characters: string[];
  stages: Array<{ label: string; detail: string; ready: boolean }>;
  runtimeLabel: string;
  runtimeSeconds: number;
  sceneCount: number;
  chunkCount: number;
  outputShape: string;
};

type RenderPlan = {
  providerModel: string;
  modelLabel: string;
  aspectRatio: string;
  generationResolution: string;
  providerQuality: string;
  deliveryResolution: string;
  averageChunkSeconds: number;
  estimatedCredits: number;
  sceneCount: number;
  chunkCount: number;
  payloadLines: Array<{ label: string; value: string }>;
};

type ReferenceKind = "character_card" | "scene_reference";

type ReferenceStatus = "empty" | "planned" | "generating" | "ready" | "partial" | "not_configured" | "error";

type ReferenceAssetStatus = "planned" | "generating" | "generated" | "uploaded" | "missing" | "not_configured" | "failed";

type ReferenceAsset = {
  id: string;
  kind: ReferenceKind;
  label: string;
  role?: string;
  characterName?: string;
  sceneId?: string;
  sceneLabel?: string;
  fileName: string;
  plannedStorageKey: string;
  storageKey: string;
  sourceUrl: string;
  status: ReferenceAssetStatus;
  provider: "planned" | "openai" | "user";
  sourceMode: "planned" | "chatgpt_image" | "user_upload";
  locked: boolean;
  sourceOfTruth: boolean;
};

type ReferenceRole = {
  token: string;
  role: string;
  label: string;
  assetId: string;
  storageKey: string;
  status: ReferenceAssetStatus;
  sourceMode: ReferenceAsset["sourceMode"];
};

type UploadedAssetResponse = {
  ok: boolean;
  status: string;
  message?: string;
  asset?: {
    storageKey?: string | null;
    sourceUrl?: string;
    fileName?: string;
    provider?: string;
    sourceMode?: string;
    cloudStatus?: string;
  };
};

type ReferenceRegistry = {
  characterCards: ReferenceAsset[];
  sceneReferences: ReferenceAsset[];
  status: ReferenceStatus;
  message: string;
  updatedAt: string | null;
};

type ReferenceCounts = {
  total: number;
  ready: number;
  planned: number;
  characterCards: number;
  sceneReferences: number;
};

type CharacterSpec = {
  id: string;
  name: string;
  role: string;
  description: string;
  archetype: string;
  wardrobe: string;
  fileName: string;
  locked: boolean;
  sourceOfTruth: boolean;
  cardStatus: string;
};

type SceneSpec = {
  id: string;
  sceneId: string;
  heading: string;
  slugline: string;
  location: string;
  summary: string;
  timeOfDay: string;
  mood: string;
  fileName: string;
  locked: boolean;
  sourceOfTruth: boolean;
  referenceStatus: string;
};

type ScreenplayDialogue = {
  character: string;
  line: string;
  delivery: string;
};

type ScreenplayCharacter = {
  id: string;
  name: string;
  role: "lead" | "support" | "antagonist" | "minor";
  look: string;
  wardrobe: string;
  voice: string;
  want: string;
  need: string;
  flaw: string;
  tell: string;
};

type ScreenplayProp = {
  id: string;
  name: string;
  description: string;
  continuity: string;
};

type ScreenplayScene = {
  id: string;
  slugline: string;
  location: string;
  timeOfDay: string;
  summary: string;
  goal: string;
  conflict: string;
  turn: string;
  exitState: string;
  dialogue: ScreenplayDialogue[];
};

type ScreenplayShot = {
  id: string;
  sceneId: string;
  durationSeconds: number;
  purpose: string;
  shotType: string;
  action: string;
  dialogue: ScreenplayDialogue | null;
  sound: string;
  entryState: string;
  exitState: string;
  screenDirection: string;
};

type Screenplay = {
  title: string;
  logline: string;
  theme: string;
  comicEngine: string;
  world: { era: string; look: string; rules: string[] };
  characters: ScreenplayCharacter[];
  beats?: Array<{ id: string; label: string; purpose: string; turn: string }>;
  props: ScreenplayProp[];
  scenes: ScreenplayScene[];
  shots: ScreenplayShot[];
  openingImage: string;
  finalImage: string;
  hook: string;
  notes: string[];
  runtimeSeconds: number;
  generatedAt: string;
  brief: { seed: string; genreId: string; formatId: string; spokenDialogue: boolean };
};

type ScriptStatus = "idle" | "generating" | "refining" | "ready" | "error";

type ScriptGenerateResponse = {
  ok: boolean;
  status: string;
  mode?: string;
  provider?: string;
  model?: string;
  message?: string;
  screenplay?: Screenplay;
};

const STORAGE_KEY = "kinetix_director_console_v1";
const REFERENCE_STORAGE_KEY = "kinetix_director_console_refs_v1";
const SCRIPT_STORAGE_KEY = "kinetix_director_console_script_v1";
const API_BASE = (import.meta.env.VITE_KINETIX_API_BASE || "").replace(/\/$/, "");
const DEFAULT_RENDER_CAP = 250;

const defaultState: StoryState = {
  projectMode: "new_story",
  sourceProject: "neon_courier",
  inheritCharacters: true,
  inheritVisualDNA: true,
  inheritDirector: true,
  inheritAspect: true,
  inheritEvolink: true,
  plotFlex: 3,
  seed: "A burned-out courier in a rain-soaked future city discovers the package they are carrying contains a memory that should not exist.",
  format: "short_film",
  aspect: "9x16",
  genre: "sci_fi",
  tone: "propulsive",
  audience: "streaming",
  structure: "three_act",
  arc: "reluctant_hero",
  stakes: "ticking_clock",
  twist: "memory_reveal",
  era: "near_future",
  visual: "neo_noir",
  director: "atmospheric_scale",
  characterDepth: 4,
  castSize: 3,
  leadType: "wounded_pro",
  supportType: "loyal_skeptic",
  antagonistType: "friendly_threat",
  characterLook: "cinematic_portrait",
  runtime: 180,
  chunkProfile: "balanced_10",
  imageModel: true,
  characterCards: true,
  faceLock: true,
  evolinkModel: "seedance_2_0_mini",
  videoResolution: "720p",
  autoUpscale: true,
  storyboards: true,
  voicePlan: true,
  audioMode: "story_mix",
  audioSfx: true,
  audioDialogue: true,
  audioVoiceover: true,
  audioScore: true,
  audioMusicBed: true,
  pitchDeck: false,
  renderPackage: true,
  spokenDialogue: true,
  scriptProvider: "auto",
  continuityChain: true,
  seedAudioVoices: false,
  subGenre: ""
};

// Sub-genre flavors per genre — ids/labels stay in sync with the script engines.
const genreFlavors: Record<string, Option[]> = {
  comedy: [
    { id: "dark_comedy", label: "Dark Comedy", detail: "Taboo pressure, bureaucratic calm." },
    { id: "modern_cringe", label: "Modern Cringe", detail: "Social traps, held silences." },
    { id: "old_school_slapstick", label: "Old-School Slapstick", detail: "Props, doors, immaculate timing." },
    { id: "comedy_80s", label: "80s Comedy", detail: "High concept, montages, heart." },
    { id: "comedy_90s", label: "90s Comedy", detail: "Slacker buddies, deadpan." },
    { id: "parody", label: "Parody", detail: "Perfect imitation, one absurdity." }
  ],
  action: [
    { id: "action_80s", label: "80s Action", detail: "Quips after impact, practical stunts." },
    { id: "buddy_cop_90s", label: "90s Buddy Cop", detail: "The argument is the plot." },
    { id: "modern_tactical", label: "Modern Tactical", detail: "Procedure as suspense." },
    { id: "martial_arts", label: "Martial Arts", detail: "Fights as conversations." },
    { id: "vehicular", label: "Chase Picture", detail: "Route, gap, obstacle." }
  ],
  action_comedy: [
    { id: "buddy_cop", label: "Buddy Cop", detail: "Argument through the gunfight." },
    { id: "spy_comedy", label: "Spy Comedy", detail: "Immaculate grammar, oblivious lead." },
    { id: "action_80s_vhs", label: "80s VHS", detail: "Mullets, montages, living final tableau." },
    { id: "family_chaos", label: "Family Chaos", detail: "School run as car chase." }
  ],
  mockumentary: [
    { id: "office_style", label: "Office Style", detail: "Rituals as battlegrounds." },
    { id: "true_crime_spoof", label: "True-Crime Spoof", detail: "Prestige gravity, trivial crime." },
    { id: "sports_doc", label: "Sports Doc", detail: "Underdog arc, lowest stakes." },
    { id: "nature_doc", label: "Nature Doc", detail: "Hushed reverence for humans." }
  ],
  thriller: [
    { id: "psychological", label: "Psychological", detail: "Reality degrades in increments." },
    { id: "conspiracy", label: "Conspiracy", detail: "Every helper might be a handler." },
    { id: "home_invasion", label: "Home Invasion", detail: "The house becomes the trap." },
    { id: "techno", label: "Techno", detail: "The system is the antagonist." }
  ],
  sci_fi: [
    { id: "cyberpunk", label: "Cyberpunk", detail: "High tech, low life." },
    { id: "space_opera", label: "Space Opera", detail: "Mythic scale, personal cost." },
    { id: "retro_future", label: "Retro-Future", detail: "Yesterday's tomorrow." },
    { id: "first_contact", label: "First Contact", detail: "The alien is a mirror." }
  ],
  horror: [
    { id: "slasher", label: "Slasher", detail: "Rules-driven dread." },
    { id: "supernatural", label: "Supernatural", detail: "The haunting feeds on guilt." },
    { id: "found_footage", label: "Found Footage", detail: "The camera has a reason." },
    { id: "creature", label: "Creature", detail: "Silhouette, evidence, part, whole." },
    { id: "folk", label: "Folk Horror", detail: "Daylight dread, smiling community." }
  ],
  romance: [
    { id: "romcom", label: "Rom-Com", detail: "Banter as thesis." },
    { id: "slow_burn", label: "Slow Burn", detail: "Proximity without permission." },
    { id: "second_chance", label: "Second Chance", detail: "History in shorthand." },
    { id: "forbidden", label: "Forbidden", detail: "Structural obstacles, stolen time." }
  ],
  fantasy: [
    { id: "high_fantasy", label: "High Fantasy", detail: "The map matters." },
    { id: "urban_fantasy", label: "Urban Fantasy", detail: "Magic leaks through mundane seams." },
    { id: "fairy_tale", label: "Grimm Tale", detail: "Bargains, rules of three." },
    { id: "sword_sorcery", label: "Sword & Sorcery", detail: "A debt, a blade, a bounty." }
  ],
  drama: [
    { id: "family", label: "Family", detail: "The dinner table is the arena." },
    { id: "coming_of_age", label: "Coming of Age", detail: "Firsts carry the plot." },
    { id: "workplace", label: "Workplace", detail: "Procedure made dramatic." },
    { id: "quiet_indie", label: "Quiet Indie", detail: "Seismic interiors, patient camera." }
  ],
  heist: [
    { id: "one_last_job", label: "One Last Job", detail: "Retirement is the MacGuffin." },
    { id: "con_game", label: "Con Game", detail: "The audience is the final mark." },
    { id: "casino_glam", label: "Casino Glam", detail: "Opulence justifies the taking." },
    { id: "smalltown_caper", label: "Small-Town Caper", detail: "Amateur crew, borrowed tools." }
  ],
  mystery: [
    { id: "hardboiled_noir", label: "Hardboiled Noir", detail: "The city is complicit." },
    { id: "cozy", label: "Cozy", detail: "Deduction on-screen, violence off." },
    { id: "whodunit_ensemble", label: "Whodunit", detail: "One location, all suspects." },
    { id: "missing_person", label: "Missing Person", detail: "The absence testifies." }
  ]
};

const scriptProviders: Option[] = [
  { id: "auto", label: "Auto", detail: "Uses whichever script key the backend has; prefers Claude when both are set.", meta: "default" },
  { id: "anthropic", label: "Claude", detail: "Anthropic Claude Sonnet writes the screenplay (needs KINETIX_FILM_ANTHROPIC_API_KEY).", meta: "sonnet" },
  { id: "openai", label: "OpenAI", detail: "The existing OpenAI key writes the screenplay (KINETIX_FILM_OPENAI_API_KEY).", meta: "gpt" }
];

const projectModes: Option[] = [
  { id: "new_story", label: "New Story", detail: "Start clean with no inherited project settings.", meta: "fresh" },
  { id: "reuse_setup", label: "Reuse Setup", detail: "Copy the look, delivery, and production settings from a previous project.", meta: "same setup" },
  { id: "continue_series", label: "Continue Miniseries", detail: "Keep the bible, cast, world rules, and style for a new episode.", meta: "episode" },
  { id: "sub_project", label: "Sub-Project", detail: "Create a trailer, teaser, side story, or alt cut inside an existing project.", meta: "nested" }
];

const sourceProjects: Option[] = [
  { id: "neon_courier", label: "Neon Courier", detail: "Near-future noir, vertical-first story system.", meta: "miniseries" },
  { id: "dark_current", label: "Dark Current", detail: "Sci-fi thriller with memory tech, noir rain, and 16:9 delivery.", meta: "film" },
  { id: "last_dumpling", label: "The Last Dumpling", detail: "Stylized comedy-action continuity and character ensemble.", meta: "short" },
  { id: "custom_project", label: "Custom Saved Project", detail: "Use the active saved project once backend project storage is connected.", meta: "saved" }
];

const formats: Option[] = [
  { id: "trailer", label: "Trailer", detail: "High-impact arc with a hook, escalation, and final button.", meta: "0:30-2:00" },
  { id: "short_film", label: "Short Film", detail: "Complete story with character turn, midpoint, and payoff.", meta: "2-12 min" },
  { id: "teaser", label: "Teaser", detail: "Mystery-first proof of concept built around one unforgettable image.", meta: "0:15-0:45" },
  { id: "tv_intro", label: "TV Show Intro", detail: "Rewatchable title-sequence emblem: one visual system, cast icons, title-plate finish.", meta: "0:30-1:00" },
  { id: "pilot_scene", label: "Pilot Scene", detail: "A self-contained scene that sells a larger world.", meta: "1-4 min" },
  { id: "pitch_reel", label: "Pitch Reel", detail: "Story spine, mood, and visual promise for buyers or collaborators.", meta: "1-3 min" },
  { id: "social_series", label: "Social Series", detail: "Repeatable episodic beats for vertical releases.", meta: "multi-part" },
  { id: "commercial", label: "In-World Commercial", detail: "An ad from inside your story's world: problem, promise, proof, button.", meta: "0:15-1:00" },
  { id: "stinger", label: "Post-Credits Stinger", detail: "One-joke epilogue that recontextualizes the film or teases the sequel.", meta: "0:10-0:30" }
];

const formatPresets: Record<string, { minRuntime: number; maxRuntime: number; fallbackRuntime: number; chunkProfile: string }> = {
  trailer: { minRuntime: 30, maxRuntime: 120, fallbackRuntime: 60, chunkProfile: "fast_6" },
  teaser: { minRuntime: 15, maxRuntime: 45, fallbackRuntime: 30, chunkProfile: "fast_6" },
  short_film: { minRuntime: 120, maxRuntime: 720, fallbackRuntime: 300, chunkProfile: "balanced_10" },
  tv_intro: { minRuntime: 30, maxRuntime: 60, fallbackRuntime: 45, chunkProfile: "fast_6" },
  pilot_scene: { minRuntime: 60, maxRuntime: 240, fallbackRuntime: 120, chunkProfile: "balanced_10" },
  pitch_reel: { minRuntime: 60, maxRuntime: 180, fallbackRuntime: 90, chunkProfile: "balanced_10" },
  social_series: { minRuntime: 10, maxRuntime: 45, fallbackRuntime: 22, chunkProfile: "fast_6" },
  commercial: { minRuntime: 15, maxRuntime: 60, fallbackRuntime: 30, chunkProfile: "fast_6" },
  stinger: { minRuntime: 10, maxRuntime: 30, fallbackRuntime: 15, chunkProfile: "fast_6" }
};

const visualGradeMap: Record<string, string> = {
  neo_noir: "noir",
  bright_comedy: "comedy_pop",
  propulsive_action: "bayhem",
  elevated_horror: "horror_dread",
  storybook: "warm_indie",
  retro_vhs: "documentary_grit",
  warm_indie: "warm_indie",
  techno_thriller: "teal_orange"
};

/** Finishing spec for the master stitch, derived from the project's visual DNA.
 *  Maps to the render engine's look filter: grade, grain, 24fps conform, fade
 *  tails, and loudness normalization; scope aspect gets a cinematic letterbox. */
function buildStitchLook(state: StoryState): Record<string, unknown> {
  const look: Record<string, unknown> = {
    grade: visualGradeMap[state.visual] || "warm_indie",
    grain: 6,
    fps24: true,
    fade: 0.5,
    loudnorm: true
  };
  if (state.aspect === "239x1") look.letterbox = "2.39";
  return look;
}

function formatSelectionPatch(formatId: string, currentRuntime: number): Partial<StoryState> {
  const preset = formatPresets[formatId];
  if (!preset) return { format: formatId };
  const runtime = currentRuntime < preset.minRuntime || currentRuntime > preset.maxRuntime
    ? preset.fallbackRuntime
    : currentRuntime;
  return { format: formatId, runtime, chunkProfile: preset.chunkProfile };
}

const aspects: Option[] = [
  { id: "9x16", label: "9:16", detail: "Vertical", meta: "TikTok / Reels" },
  { id: "16x9", label: "16:9", detail: "Landscape", meta: "YouTube / TV" },
  { id: "1x1", label: "1:1", detail: "Square", meta: "Feed-safe" },
  { id: "4x5", label: "4:5", detail: "Portrait", meta: "Instagram" },
  { id: "239x1", label: "2.39:1", detail: "Scope", meta: "Cinematic" }
];

const genres: Option[] = [
  { id: "comedy", label: "Comedy", detail: "Clear premise, reversals, escalating jokes.", tone: "comedy" },
  { id: "action", label: "Action", detail: "Momentum, set pieces, physical stakes.", tone: "action" },
  { id: "action_comedy", label: "Action Comedy", detail: "Set pieces that are jokes; gags with real stakes.", tone: "action" },
  { id: "mockumentary", label: "Mockumentary", detail: "Interview vs evidence; the cut is the punchline.", tone: "comedy" },
  { id: "thriller", label: "Thriller", detail: "Pressure, paranoia, dangerous reveals.", tone: "thriller" },
  { id: "sci_fi", label: "Sci-Fi", detail: "Speculative hook, human cost, world rules.", tone: "sci" },
  { id: "horror", label: "Horror", detail: "Dread, containment, creature or curse logic.", tone: "horror" },
  { id: "romance", label: "Romance", detail: "Chemistry, longing, obstacle, confession.", tone: "romance" },
  { id: "fantasy", label: "Fantasy", detail: "Mythic object, quest rules, wonder.", tone: "fantasy" },
  { id: "drama", label: "Drama", detail: "Emotional wound, choice, consequence.", tone: "drama" },
  { id: "heist", label: "Heist", detail: "Crew, target, plan, complication.", tone: "heist" },
  { id: "mystery", label: "Mystery", detail: "Question engine, suspects, clue trail.", tone: "mystery" }
];

const tones: Option[] = [
  { id: "funny", label: "Funny", detail: "Light, surprising, joke-forward." },
  { id: "propulsive", label: "Propulsive", detail: "Fast, clear, high urgency." },
  { id: "emotional", label: "Emotional", detail: "Tender, vulnerable, cathartic." },
  { id: "dark", label: "Dark", detail: "Sharp edges and moral pressure." },
  { id: "surreal", label: "Surreal", detail: "Dream logic and strange images." },
  { id: "family", label: "Family", detail: "Warm, accessible, broad appeal." }
];

const audiences: Option[] = [
  { id: "streaming", label: "Streaming", detail: "Prestige clarity with a strong thumbnail moment." },
  { id: "social", label: "Social", detail: "Immediate hook, bold visual turns, repeatable parts." },
  { id: "festival", label: "Festival", detail: "Original voice, emotional specificity, artful ending." },
  { id: "brand", label: "Brand", detail: "Product-safe story with a memorable benefit." }
];

const structures: Option[] = [
  { id: "three_act", label: "3 Act", detail: "Setup, confrontation, resolution." },
  { id: "hero_journey", label: "Hero Journey", detail: "Call, ordeal, transformation." },
  { id: "save_cat", label: "Save the Cat", detail: "Clean commercial beat ladder." },
  { id: "mystery_box", label: "Mystery Box", detail: "Question, clue, reversal, answer." },
  { id: "one_location", label: "Contained", detail: "One place, rising pressure." },
  { id: "heist_clock", label: "Heist Clock", detail: "Plan, crew, timer, betrayal." }
];

const arcs: Option[] = [
  { id: "reluctant_hero", label: "Reluctant Hero", detail: "Avoids the call until the cost becomes personal." },
  { id: "redemption", label: "Redemption", detail: "A past failure becomes the path to courage." },
  { id: "obsession", label: "Obsession", detail: "A need turns dangerous and changes the character." },
  { id: "underdog", label: "Underdog", detail: "Small player wins through wit and heart." },
  { id: "rivals_family", label: "Rivals to Family", detail: "Opponents become the only people who understand each other." },
  { id: "fall_grace", label: "Fall from Grace", detail: "Power, temptation, and a consequential mistake." }
];

const leadTypes: Option[] = [
  { id: "wounded_pro", label: "Wounded Pro", detail: "Competent, guarded, hiding the reason they quit." },
  { id: "ordinary_person", label: "Ordinary Person", detail: "Relatable outsider pulled into a bigger world." },
  { id: "chaotic_genius", label: "Chaotic Genius", detail: "Brilliant instincts, terrible impulse control." },
  { id: "quiet_survivor", label: "Quiet Survivor", detail: "Underestimated, observant, harder to break than expected." },
  { id: "comic_striver", label: "Comic Striver", detail: "Wants respect, creates trouble, accidentally grows." },
  { id: "fallen_star", label: "Fallen Star", detail: "Former icon trying to get one clean win." }
];

const supportTypes: Option[] = [
  { id: "loyal_skeptic", label: "Loyal Skeptic", detail: "Challenges the lead, then becomes essential." },
  { id: "wild_card", label: "Wild Card", detail: "Unpredictable helper with the best worst idea." },
  { id: "heart_anchor", label: "Heart Anchor", detail: "The emotional reason the story matters." },
  { id: "expert_guide", label: "Expert Guide", detail: "Knows the world rules and the hidden cost." },
  { id: "comic_relief", label: "Comic Relief", detail: "Pressure valve with useful instincts." },
  { id: "rival_partner", label: "Rival Partner", detail: "Competes with the lead until the mission fuses them." }
];

const antagonistTypes: Option[] = [
  { id: "friendly_threat", label: "Friendly Threat", detail: "Smiling, helpful, quietly controlling the trap." },
  { id: "mirror_villain", label: "Mirror Villain", detail: "Same wound as the hero, worse conclusion." },
  { id: "system_pressure", label: "System Pressure", detail: "Institution, algorithm, family, or city as antagonist." },
  { id: "monster_rule", label: "Monster Rule", detail: "A creature or curse with clean cinematic rules." },
  { id: "betrayer", label: "Betrayer", detail: "Trust is the weapon; reveal lands late." },
  { id: "force_nature", label: "Force of Nature", detail: "Unstoppable, simple desire, terrifying presence." }
];

const characterLooks: Option[] = [
  { id: "cinematic_portrait", label: "Cinematic Portrait", detail: "Clean face card, wardrobe, expression, lighting." },
  { id: "full_body_costume", label: "Full Body Costume", detail: "Wardrobe silhouette and prop-ready continuity." },
  { id: "turnaround_sheet", label: "Turnaround Sheet", detail: "Front, side, back, detail notes for consistency." },
  { id: "emotion_sheet", label: "Emotion Sheet", detail: "Neutral, fear, joy, anger, resolve." },
  { id: "cast_lineup", label: "Cast Lineup", detail: "Lead and support scale, color, relationship blocking." }
];

const stakes: Option[] = [
  { id: "ticking_clock", label: "Ticking Clock", detail: "A deadline turns every scene into a choice." },
  { id: "secret_exposed", label: "Secret Exposed", detail: "The truth will destroy the world the hero built." },
  { id: "family_risk", label: "Family at Risk", detail: "The emotional cost is immediate and personal." },
  { id: "wrong_accused", label: "Wrongfully Accused", detail: "The hero must solve the problem while being hunted." },
  { id: "survival", label: "Survival", detail: "Escape, endure, outsmart, or die." },
  { id: "public_spectacle", label: "Public Spectacle", detail: "The climax happens where everyone can see." }
];

const twists: Option[] = [
  { id: "memory_reveal", label: "Memory Reveal", detail: "A remembered truth reframes the whole story." },
  { id: "false_ally", label: "False Ally", detail: "The helper is steering the danger." },
  { id: "hidden_identity", label: "Hidden Identity", detail: "A character is not who the audience thinks." },
  { id: "time_loop", label: "Time Loop", detail: "Repetition becomes a clue, not a gimmick." },
  { id: "moral_reversal", label: "Moral Reversal", detail: "The apparent villain has the clearer reason." },
  { id: "final_image", label: "Final Image Payoff", detail: "The last shot answers the first question." }
];

const eras: Option[] = [
  { id: "modern", label: "Modern", detail: "Today, grounded tech and recognizable spaces.", meta: "2020s" },
  { id: "near_future", label: "Near Future", detail: "Tomorrow's world, still emotionally familiar.", meta: "2035-2045" },
  { id: "1980s", label: "1980s", detail: "Analog texture, malls, synths, practical light.", meta: "retro" },
  { id: "1970s", label: "1970s", detail: "Grit, zooms, grain, lived-in rooms.", meta: "period" },
  { id: "1940s", label: "1940s", detail: "Noir silhouettes, wartime pressure, smoke.", meta: "classic" },
  { id: "mythic", label: "Mythic", detail: "Timeless villages, ritual, prophecy, old powers.", meta: "legend" }
];

const visuals: Option[] = [
  { id: "neo_noir", label: "Neo Noir", detail: "Rain, neon, silhouettes, high contrast.", meta: "moody" },
  { id: "bright_comedy", label: "Bright Comedy", detail: "Clean framing, expressive faces, warm color.", meta: "friendly" },
  { id: "propulsive_action", label: "Propulsive Action", detail: "Hard light, motion blur, kinetic geography.", meta: "impact" },
  { id: "elevated_horror", label: "Elevated Horror", detail: "Stillness, negative space, ritual texture.", meta: "dread" },
  { id: "storybook", label: "Storybook Fantasy", detail: "Painterly light, rich worlds, tactile props.", meta: "wonder" },
  { id: "retro_vhs", label: "Retro VHS", detail: "Tape bloom, practical sets, saturated night.", meta: "analog" },
  { id: "warm_indie", label: "Warm Indie", detail: "Natural light, handheld intimacy, real rooms.", meta: "human" },
  { id: "techno_thriller", label: "Techno Thriller", detail: "Glass, terminals, cold contrast, precise blocking.", meta: "sleek" }
];

const directors: Option[] = [
  { id: "atmospheric_scale", label: "Atmospheric Scale", detail: "Patient tension, monumental frames, intimate faces." },
  { id: "kinetic_practical", label: "Kinetic Practical Action", detail: "Readable geography, practical stunts, urgent camera." },
  { id: "intimate_handheld", label: "Intimate Handheld", detail: "Human texture, close reactions, lived-in blocking." },
  { id: "bright_ensemble", label: "Bright Ensemble Comedy", detail: "Clean staging, punchy timing, expressive groups." },
  { id: "storybook_wonder", label: "Storybook Wonder", detail: "Graphic compositions, tactile worlds, poetic reveals." },
  { id: "surreal_pressure", label: "Surreal Pressure", detail: "Unsettling rhythm, symbolic images, uncanny sound." }
];

const runtimeOptions: Option[] = [
  { id: "15", label: "0:15", detail: "Fast ad-style proof." },
  { id: "30", label: "0:30", detail: "Teaser proof." },
  { id: "60", label: "1:00", detail: "Trailer spine." },
  { id: "120", label: "2:00", detail: "Rich trailer." },
  { id: "180", label: "3:00", detail: "Short scene." },
  { id: "300", label: "5:00", detail: "Short film." },
  { id: "600", label: "10:00", detail: "Full short." }
];

function clampRuntimeSeconds(value: number, fallback = 60): number {
  const seconds = Math.round(Number(value));
  return Number.isFinite(seconds) && seconds > 0 ? seconds : fallback;
}

const chunkProfiles: Option[] = [
  { id: "fast_6", label: "6s Fast", detail: "More clips, quick retries, best for draft coverage and risky shots.", meta: "fast" },
  { id: "balanced_10", label: "10s Balanced", detail: "Fewer resets while keeping failed clips cheaper to retry.", meta: "balanced" },
  { id: "continuity_15", label: "15s Continuity", detail: "Longer one-take beats for smoother motion and fewer repeated actions.", meta: "smooth" }
];

const audioModes: Option[] = [
  { id: "story_mix", label: "Story Mix", detail: "Foley, score/music, dialogue, and optional narrator/VO are available when the beat needs them.", meta: "default" },
  { id: "character_lines", label: "Character Lines", detail: "Keep scripted character speech and motivated scene audio; narrator is optional and off by default.", meta: "dialogue" },
  { id: "natural_foley", label: "Natural Foley", detail: "Scene-first footsteps, breathing, props, room tone, and motivated ambience.", meta: "sfx" },
  { id: "dialogue_scene", label: "Dialogue Scene", detail: "Prioritize scripted speech, mouth performance, and clean dialogue support.", meta: "script" },
  { id: "trailer_mix", label: "Trailer Mix", detail: "Score bed, hits, risers, optional trailer narrator/VO, and selected dialogue for promos.", meta: "promo" },
  { id: "silent_planning", label: "Silent Planning", detail: "No generated audio for boards, previz, or later manual sound design.", meta: "silent" }
];

const evolinkModels: Option[] = [
  { id: "seedance_2_0_mini", label: "Seedance 2.0 Mini", detail: "Faster EvoLink drafts, cheaper iteration, good for previews.", meta: "draft" },
  { id: "seedance_2_0", label: "Seedance 2.0", detail: "Higher-quality EvoLink renders for final story video passes.", meta: "final" }
];

const videoResolutions: Option[] = [
  { id: "480p", label: "480p", detail: "Fast draft generation.", meta: "draft" },
  { id: "720p", label: "720p", detail: "Balanced preview generation.", meta: "review" },
  { id: "1080p", label: "1080p", detail: "High-quality full-model generation.", meta: "hd" },
  { id: "4k", label: "4K", detail: "Maximum full-model generation.", meta: "max" }
];

const evolinkModelResolutionIds: Record<string, string[]> = {
  seedance_2_0_mini: ["480p", "720p"],
  seedance_2_0: ["480p", "720p", "1080p", "4k"]
};

const renderGateOptions: Array<{ id: RenderGateKey; label: string; detail: string }> = [
  { id: "scriptRights", label: "Script Rights", detail: "Story, dialogue, and source material are clear for generation." },
  { id: "likenessRights", label: "Likeness Rights", detail: "Character references and faces are approved for video use." },
  { id: "voiceRights", label: "Voice Rights", detail: "Voice direction, audio references, and generated speech are approved." },
  { id: "brandIpRights", label: "Brand/IP Rights", detail: "Brands, logos, franchise elements, and protected marks are cleared." },
  { id: "humanReview", label: "Human Review", detail: "The package payload has been reviewed before provider spend." },
  { id: "finalConfirm", label: "Final Confirm", detail: "The approved cap and selected render scope are intentional." }
];

const defaultRenderGates: RenderGates = {
  scriptRights: false,
  likenessRights: false,
  voiceRights: false,
  brandIpRights: false,
  humanReview: false,
  finalConfirm: false
};

const kinetixScriptSkillSource: SkillSource = {
  label: "Kinetix Screenwriting Skill",
  scope: "Built-in Director Console skill",
  rule: "Genre-specific craft, but/therefore causality, want/need/flaw characters, continuity-chained shot lists, and dialogue discipline.",
  source: "/api/script/generate"
};

const scriptSkillSources: SkillSource[] = [
  {
    label: "Story Architecture",
    scope: "Public Claude skill scan",
    rule: "Use therefore/but causality, make every scene change something, and track setup/payoff.",
    source: "haowjy/creative-writing-skills"
  },
  {
    label: "Scene Construction",
    scope: "Public Claude skill scan",
    rule: "Enter in action, make dialogue do more than one job, and match transition weight to skipped story.",
    source: "haowjy/creative-writing-skills"
  },
  {
    label: "AI Video Storyboard",
    scope: "Public skill scan",
    rule: "For videos over one clip, every shot needs purpose, shared visual language, action, and audio direction.",
    source: "aicontentskills/ai-video-storyboard-skill"
  },
  {
    label: "Kinetix Production Agents",
    scope: "Repo pipeline",
    rule: "Protect objective, obstacle, subtext, shot purpose, reaction beats, edit purpose, and motivated transitions.",
    source: "film_pipeline/planner.py"
  }
];

const sourceProjectPresets: Record<string, Partial<StoryState>> = {
  neon_courier: {
    genre: "sci_fi",
    tone: "propulsive",
    era: "near_future",
    visual: "neo_noir",
    director: "atmospheric_scale",
    aspect: "9x16",
    evolinkModel: "seedance_2_0_mini",
    videoResolution: "720p",
    characterDepth: 4,
    castSize: 3,
    leadType: "wounded_pro",
    supportType: "loyal_skeptic",
    antagonistType: "friendly_threat",
    characterLook: "cinematic_portrait"
  },
  dark_current: {
    genre: "thriller",
    tone: "dark",
    era: "near_future",
    visual: "techno_thriller",
    director: "surreal_pressure",
    aspect: "16x9",
    evolinkModel: "seedance_2_0",
    videoResolution: "1080p",
    characterDepth: 5,
    castSize: 4,
    leadType: "quiet_survivor",
    supportType: "expert_guide",
    antagonistType: "system_pressure",
    characterLook: "turnaround_sheet"
  },
  last_dumpling: {
    genre: "comedy",
    tone: "funny",
    era: "mythic",
    visual: "storybook",
    director: "bright_ensemble",
    aspect: "16x9",
    evolinkModel: "seedance_2_0",
    videoResolution: "720p",
    characterDepth: 4,
    castSize: 5,
    leadType: "comic_striver",
    supportType: "wild_card",
    antagonistType: "force_nature",
    characterLook: "cast_lineup"
  },
  custom_project: {}
};

function findOption(options: Option[], id: string): Option {
  return options.find((option) => option.id === id) || options[0];
}

function availableVideoResolutions(modelId: string): Option[] {
  const ids = evolinkModelResolutionIds[modelId] || evolinkModelResolutionIds.seedance_2_0_mini;
  return videoResolutions.filter((resolution) => ids.includes(resolution.id));
}

function normalizeVideoResolution(modelId: string, resolutionId: string): string {
  const available = availableVideoResolutions(modelId);
  return available.some((resolution) => resolution.id === resolutionId) ? resolutionId : available[available.length - 1].id;
}

function upscaleTargetForResolution(resolutionId: string): string {
  if (resolutionId === "480p") return "720p";
  if (resolutionId === "720p") return "1080p";
  if (resolutionId === "1080p") return "4K";
  return "4K";
}

function resolutionLabel(resolutionId: string): string {
  return findOption(videoResolutions, resolutionId).label;
}

function providerQualityForResolution(resolutionId: string): string {
  return resolutionId === "4k" ? "4K" : resolutionId;
}

function providerModelForEvolink(modelId: string): string {
  return modelId === "seedance_2_0_mini"
    ? "seedance-2.0-fast-reference-to-video"
    : "seedance-2.0-reference-to-video";
}

function providerAspectRatio(aspectId: string): string {
  if (aspectId === "9x16") return "9:16";
  if (aspectId === "16x9") return "16:9";
  if (aspectId === "1x1") return "1:1";
  if (aspectId === "4x5") return "4:5";
  if (aspectId === "239x1") return "21:9";
  return "16:9";
}

function targetChunkSeconds(state: StoryState): number {
  if (state.chunkProfile === "fast_6") return 6;
  if (state.chunkProfile === "continuity_15") return 15;
  return 10;
}

function minimumChunkCount(state: StoryState): number {
  return state.chunkProfile === "continuity_15" && state.runtime <= 45 ? 2 : 3;
}

function minimumSceneCount(state: StoryState): number {
  return state.chunkProfile === "continuity_15" && state.runtime <= 45 ? 2 : 3;
}

// Formats with a fixed dramatic shape get a fixed scene target — a stinger is ONE
// scene by definition, an intro is three movements — instead of the duration math.
const formatSceneTargets: Record<string, number> = {
  stinger: 1,
  teaser: 1,
  pilot_scene: 1,
  social_series: 2,
  tv_intro: 3,
  commercial: 3
};

function plannedSceneCount(state: StoryState): number {
  const fixed = formatSceneTargets[state.format];
  if (fixed) return fixed;
  return Math.max(minimumSceneCount(state), Math.ceil(state.runtime / (state.format === "trailer" ? 22 : 42)));
}

function plannedChunkCount(state: StoryState): number {
  return Math.max(minimumChunkCount(state), Math.ceil(state.runtime / targetChunkSeconds(state)));
}

function spokenDialogueActive(state: StoryState): boolean {
  return Boolean(state.spokenDialogue && state.audioDialogue && state.voicePlan && state.audioMode !== "silent_planning");
}

function buildScriptBrief(state: StoryState) {
  const format = findOption(formats, state.format);
  const genre = findOption(genres, state.genre);
  const tone = findOption(tones, state.tone);
  const audience = findOption(audiences, state.audience);
  const structure = findOption(structures, state.structure);
  const arc = findOption(arcs, state.arc);
  const stakesOption = findOption(stakes, state.stakes);
  const twist = findOption(twists, state.twist);
  const era = findOption(eras, state.era);
  const visual = findOption(visuals, state.visual);
  const director = findOption(directors, state.director);
  const lead = findOption(leadTypes, state.leadType);
  const support = findOption(supportTypes, state.supportType);
  const antagonist = findOption(antagonistTypes, state.antagonistType);
  return {
    seed: state.seed.trim(),
    formatId: format.id,
    formatLabel: format.label,
    formatDetail: `${format.detail} (${format.meta || ""})`.trim(),
    genreId: genre.id,
    genreLabel: genre.label,
    subGenreId: state.subGenre,
    subGenreLabel: (genreFlavors[state.genre] || []).find((flavor) => flavor.id === state.subGenre)?.label || "",
    tone: `${tone.label}: ${tone.detail}`,
    audience: `${audience.label}: ${audience.detail}`,
    structure: `${structure.label}: ${structure.detail}`,
    arc: `${arc.label}: ${arc.detail}`,
    stakes: `${stakesOption.label}: ${stakesOption.detail}`,
    twist: `${twist.label}: ${twist.detail}`,
    era: `${era.label}: ${era.detail}`,
    visualStyle: `${visual.label}: ${visual.detail}`,
    directorStyle: `${director.label}: ${director.detail}`,
    castSize: state.castSize,
    characterDepth: state.characterDepth,
    leadArchetype: `${lead.label}: ${lead.detail}`,
    supportArchetype: `${support.label}: ${support.detail}`,
    antagonistArchetype: `${antagonist.label}: ${antagonist.detail}`,
    runtimeSeconds: state.runtime,
    targetShotSeconds: targetChunkSeconds(state),
    targetShotCount: Math.min(80, plannedChunkCount(state)),
    targetSceneCount: plannedSceneCount(state),
    aspectRatio: providerAspectRatio(state.aspect),
    spokenDialogue: spokenDialogueActive(state)
  };
}

function screenplayIsStale(state: StoryState, screenplay: Screenplay | null): boolean {
  if (!screenplay) return false;
  return screenplay.brief.seed !== state.seed.trim()
    || screenplay.brief.genreId !== state.genre
    || screenplay.brief.formatId !== state.format
    || screenplay.brief.spokenDialogue !== spokenDialogueActive(state);
}

type ShotLintWarning = {
  shotId: string;
  shotIndex: number;
  kind: "repeat_verb" | "repeat_shot_type" | "near_duplicate" | "text_risk" | "speaker_run" | "format_rhythm" | "format_shape" | "format_contrast" | "chain_break" | "freeze_risk";
  message: string;
};

const SHOT_FREEZE_RISK = /\b(freeze[- ]?frame|frozen (?:pose|in place|still)|holds? (?:a|the|their) (?:pose|position)|perfectly still|motionless|stands? frozen|time stops)\b/i;

const SHOT_TEXT_RISK = /\b(sign|signage|billboard|marquee|poster|headline|lettering|subtitle|caption|label|screen (?:reads|shows|displays)|display reads|written|reads)\b/i;

const SHOT_ACTION_VERBS = [
  "sprints", "runs", "jumps", "leaps", "dives", "ducks", "dodges", "slides", "climbs", "vaults",
  "drives", "swerves", "crashes", "brakes", "accelerates", "chases", "flees", "escapes",
  "grabs", "snatches", "throws", "hurls", "catches", "drops", "lifts", "pushes", "pulls", "slams",
  "punches", "kicks", "swings", "blocks", "tackles", "wrestles", "fires", "shoots", "aims",
  "laughs", "screams", "yells", "whispers", "stares", "glares", "freezes", "turns", "spins",
  "reaches", "points", "waves", "salutes", "bows", "shrugs", "nods", "stumbles", "trips", "falls"
];

function leadActionVerb(action: string): string {
  const lower = ` ${action.toLowerCase()} `;
  return SHOT_ACTION_VERBS.find((verb) => lower.includes(` ${verb} `)) || "";
}

function normalizedShotType(shotType: string): string {
  return shotType.toLowerCase().replace(/[^a-z\s]/g, " ").replace(/\s+/g, " ").trim();
}

function actionTokens(action: string): Set<string> {
  return new Set(
    action.toLowerCase().replace(/[^a-z\s]/g, " ").split(/\s+/).filter((word) => word.length > 3)
  );
}

/** Pre-send lint pass over the shot list — flags the exact AI tells seen in past
 *  renders: adjacent near-duplicate shots, repeated action verbs, repeated shot
 *  types, readable-text requests, and long single-speaker dialogue runs. */
function lintScreenplayShots(screenplay: Screenplay): ShotLintWarning[] {
  const warnings: ShotLintWarning[] = [];
  const shots = screenplay.shots;
  shots.forEach((shot, index) => {
    if (SHOT_TEXT_RISK.test(shot.action)) {
      warnings.push({
        shotId: shot.id,
        shotIndex: index,
        kind: "text_risk",
        message: `Shot ${index + 1} asks for readable text on screen — Seedance tends to garble signs and labels. Describe them as abstract light, shape, or color instead.`
      });
    }
    if (SHOT_FREEZE_RISK.test(shot.action)) {
      warnings.push({
        shotId: shot.id,
        shotIndex: index,
        kind: "freeze_risk",
        message: `Shot ${index + 1} uses static-pose language — Seedance renders literal frozen frames. Write living holds (breath, smoke, wind) and freeze-frame in post.`
      });
    }
    if (index === 0) return;
    const prev = shots[index - 1];
    const verb = leadActionVerb(shot.action);
    if (verb && verb === leadActionVerb(prev.action)) {
      warnings.push({
        shotId: shot.id,
        shotIndex: index,
        kind: "repeat_verb",
        message: `Shots ${index} and ${index + 1} both lead with "${verb}" — adjacent clips will read as looped motion.`
      });
    }
    const typeNow = normalizedShotType(shot.shotType);
    if (typeNow && typeNow === normalizedShotType(prev.shotType)) {
      warnings.push({
        shotId: shot.id,
        shotIndex: index,
        kind: "repeat_shot_type",
        message: `Shot ${index + 1} repeats the previous clip's shot type (${shot.shotType}) — change shot size or camera energy.`
      });
    }
    const now = actionTokens(shot.action);
    const before = actionTokens(prev.action);
    if (now.size && before.size) {
      const overlap = [...now].filter((word) => before.has(word)).length;
      const union = new Set([...now, ...before]).size;
      if (union > 0 && overlap / union > 0.45) {
        warnings.push({
          shotId: shot.id,
          shotIndex: index,
          kind: "near_duplicate",
          message: `Shots ${index} and ${index + 1} read nearly identical — the #1 AI tell. Vary blocking, distance, or which character carries the beat.`
        });
      }
    }
    if (prev.exitState) {
      if (!shot.entryState) {
        warnings.push({
          shotId: shot.id,
          shotIndex: index,
          kind: "chain_break",
          message: `Shot ${index + 1} has no entryState — copy shot ${index}'s exitState forward so the clips chain.`
        });
      } else if (prev.sceneId === shot.sceneId) {
        const exitTokens = actionTokens(prev.exitState);
        const entryTokens = actionTokens(shot.entryState);
        if (exitTokens.size && entryTokens.size) {
          const shared = [...entryTokens].filter((word) => exitTokens.has(word)).length;
          const combined = new Set([...entryTokens, ...exitTokens]).size;
          if (combined > 0 && shared / combined < 0.25) {
            warnings.push({
              shotId: shot.id,
              shotIndex: index,
              kind: "chain_break",
              message: `Shots ${index} and ${index + 1} break the continuity chain — the entry state doesn't match the previous exit state (positions, props, light carry over word for word).`
            });
          }
        }
      }
    }
  });
  let runStart = 0;
  for (let index = 1; index <= shots.length; index += 1) {
    const speaker = index < shots.length ? (shots[index].dialogue?.character || "") : "";
    const previousSpeaker = shots[index - 1].dialogue?.character || "";
    if (!(speaker && speaker === previousSpeaker)) {
      if (previousSpeaker && index - runStart >= 3) {
        warnings.push({
          shotId: shots[runStart].id,
          shotIndex: runStart,
          kind: "speaker_run",
          message: `${previousSpeaker} speaks in ${index - runStart} consecutive shots (${runStart + 1}-${index}) — break the run with a reaction or action beat.`
        });
      }
      runStart = index;
    }
  }
  warnings.push(...lintScreenplayFormat(screenplay));
  return warnings.slice(0, 12);
}

// Format-aware lint; keep rules in sync with the worker and dev-server linters.
function lintScreenplayFormat(screenplay: Screenplay): ShotLintWarning[] {
  const warnings: ShotLintWarning[] = [];
  const shots = screenplay.shots;
  const scenes = screenplay.scenes;
  const formatId = screenplay.brief.formatId;
  if (!shots.length || !formatId) return warnings;
  const first = shots[0];
  if (formatId === "trailer") {
    if (first.durationSeconds > 6) {
      warnings.push({ shotId: first.id, shotIndex: 0, kind: "format_rhythm", message: `Trailer hook: the first shot runs ${first.durationSeconds}s — land the hook inside 4-6 seconds.` });
    }
    if (shots.length >= 6) {
      const third = Math.floor(shots.length / 3);
      const average = (list: ScreenplayShot[]) => list.reduce((total, shot) => total + shot.durationSeconds, 0) / list.length;
      if (average(shots.slice(third, third * 2)) >= average(shots.slice(0, third))) {
        warnings.push({ shotId: shots[third].id, shotIndex: third, kind: "format_rhythm", message: "Trailer rhythm: the escalation movement should cut FASTER than the opening — shorten the middle shots' durations." });
      }
    }
    for (let index = 2; index < shots.length; index += 1) {
      if (shots[index].sceneId === shots[index - 1].sceneId && shots[index].sceneId === shots[index - 2].sceneId) {
        warnings.push({ shotId: shots[index].id, shotIndex: index, kind: "format_contrast", message: `Three consecutive trailer beats sit in the same scene (${shots[index].sceneId}) — trailer beats should collide locations and energies.` });
        break;
      }
    }
  }
  if (formatId === "teaser" && shots.length > 8) {
    warnings.push({ shotId: shots[8].id, shotIndex: 8, kind: "format_shape", message: `Teasers want 4-8 shots; ${shots.length} dilutes the single unforgettable image.` });
  }
  if (formatId === "tv_intro") {
    const dialogueShots = shots.filter((shot) => shot.dialogue?.line);
    if (dialogueShots.length >= 2) {
      warnings.push({ shotId: dialogueShots[1].id, shotIndex: shots.indexOf(dialogueShots[1]), kind: "format_shape", message: "Title sequences are dialogue-free (one ritual catchphrase max) — move the rest of the lines into episodes." });
    }
    if (shots.length > 12) {
      warnings.push({ shotId: shots[12].id, shotIndex: 12, kind: "format_shape", message: `${shots.length} shots is heavy for an intro — 6-8 emblems at 30s, 8-12 at 60s.` });
    }
  }
  if (formatId === "social_series" && first.durationSeconds > 5) {
    warnings.push({ shotId: first.id, shotIndex: 0, kind: "format_rhythm", message: `Cold open: the first shot runs ${first.durationSeconds}s — hook inside 5 seconds or the scroll wins.` });
  }
  if (formatId === "short_film") {
    const locations = new Set(scenes.map((scene) => scene.location.toLowerCase().trim()).filter(Boolean));
    if (locations.size > 4) {
      warnings.push({ shotId: first.id, shotIndex: 0, kind: "format_shape", message: `${locations.size} distinct locations — short films hold continuity best at 2-4; merge or reuse spaces.` });
    }
  }
  if (formatId === "pilot_scene" && scenes.length > 3) {
    warnings.push({ shotId: first.id, shotIndex: 0, kind: "format_shape", message: `A pilot scene sells ONE situation; ${scenes.length} scenes reads as a mini-episode.` });
  }
  if (formatId === "stinger" && shots.length > 4) {
    warnings.push({ shotId: shots[4].id, shotIndex: 4, kind: "format_shape", message: `A stinger is one joke: 2-4 shots. ${shots.length} shots is a scene, not a stinger.` });
  }
  return warnings;
}

function screenplayToText(screenplay: Screenplay): string {
  const lines: string[] = [`${screenplay.title}`, `Logline: ${screenplay.logline}`, `Theme: ${screenplay.theme}`, ""];
  screenplay.characters.forEach((character) => {
    lines.push(`${character.name} (${character.role}): ${character.look}. Wants ${character.want}. Flaw: ${character.flaw}.`);
  });
  screenplay.scenes.forEach((scene) => {
    lines.push("", scene.slugline, scene.summary);
    if (scene.turn) lines.push(`Turn: ${scene.turn}`);
    scene.dialogue.forEach((line) => lines.push(`${line.character}: "${line.line}" (${line.delivery})`));
  });
  screenplay.shots.forEach((shot) => {
    lines.push("", `[${shot.id} · ${shot.durationSeconds}s · ${shot.shotType}] ${shot.action}${shot.dialogue ? ` ${shot.dialogue.character}: "${shot.dialogue.line}"` : ""}`);
  });
  return lines.join("\n").slice(0, 24000);
}

type CoverageNotes = {
  verdict?: string;
  verdictReason?: string;
  scores?: Record<string, number>;
  strengths?: string[];
  concerns?: string[];
};

function varyShotNote(warning: ShotLintWarning): string {
  return `Rewrite shot ${warning.shotId}: ${warning.message} Keep its story beat, screen direction, and continuity chain (entryState/exitState), but change the framing, action verb, and visual energy so adjacent clips contrast.`;
}

type RenderJobTask = {
  chunkId: string;
  label: string;
  status: string;
  durationSeconds: number;
  progress?: number;
  creditsReserved?: number;
  videoKey?: string;
  error?: string;
  chainNote?: string;
};

type RenderJob = {
  id: string;
  title: string;
  status: string;
  reservedCredits: number;
  approvedCapCredits: number;
  continuityChaining?: boolean;
  stopReason?: string;
  tasks: RenderJobTask[];
};

type ClipQcNotes = {
  score?: number;
  verdict?: string;
  overall?: string;
  strengths?: string[];
  aiTells?: Array<{ at?: string; tell?: string; fix?: string }>;
  continuity?: Array<{ at?: string; note?: string }>;
  polish?: string[];
};

type ClipQcState = {
  status: "running" | "ready" | "pending_agent" | "error";
  message: string;
  notes: ClipQcNotes | null;
};

type StitchState = {
  status: "idle" | "starting" | "running" | "done" | "failed";
  stitchId: string;
  step: string;
  message: string;
  masterKey: string;
};

const idleStitchState: StitchState = { status: "idle", stitchId: "", step: "", message: "", masterKey: "" };

function chunkProfileSummary(state: StoryState): string {
  const profile = findOption(chunkProfiles, state.chunkProfile);
  return `${profile.label}, target ${targetChunkSeconds(state)}s clips`;
}

function buildMotionDirection(state: StoryState): string {
  if (state.chunkProfile === "continuity_15") {
    return [
      "Motion direction: use locked 15-second continuity beats with a clear beginning, middle, and end inside each clip.",
      "Avoid looped running, reset poses, duplicated close-ups, and repeated action verbs between adjacent beats.",
      "For continuation renders, prefer one immediate prior video reference and keep total video reference duration under 20 seconds."
    ].join(" ");
  }
  if (state.chunkProfile === "balanced_10") {
    return [
      "Motion direction: use 10-second beat phrases with readable blocking and one distinct action turn per clip.",
      "Avoid restarting the character in the same pose or repeating the same running/turning insert across adjacent clips."
    ].join(" ");
  }
  return [
    "Motion direction: use fast 6-second coverage beats with strong visual contrast between adjacent clips.",
    "Each clip should have one clean action idea so failed drafts are cheap to rerender."
  ].join(" ");
}

function shouldGenerateAudio(state: StoryState): boolean {
  return Boolean(state.voicePlan && state.audioMode !== "silent_planning");
}

function audioModeSummary(state: StoryState): string {
  if (!state.voicePlan || state.audioMode === "silent_planning") return "Silent planning";
  const mode = findOption(audioModes, state.audioMode);
  const lanes = [
    state.audioSfx ? "SFX" : "",
    state.audioDialogue ? "dialogue" : "",
    state.audioVoiceover ? "VO" : "",
    state.audioScore ? "score" : "",
    state.audioMusicBed ? "music bed" : ""
  ].filter(Boolean);
  return `${mode.label}: ${lanes.length ? lanes.join(", ") : "no active lanes"}`;
}

function buildAudioDirection(state: StoryState, format: Option, genre: Option, tone: Option): string {
  if (!shouldGenerateAudio(state)) {
    return "Audio direction: silent planning preview. Do not generate soundtrack, dialogue, voiceover, or music for this planning pass.";
  }

  const mode = findOption(audioModes, state.audioMode);
  const lanes = [];
  if (state.audioSfx) {
    lanes.push("natural Foley and motivated sound effects such as footsteps, breath, clothing, props, room tone, doors, vehicles, weather, and location ambience");
  }
  if (state.audioDialogue) {
    lanes.push("character dialogue when the beat has approved scripted lines; keep speech clear, performable, and audio-only with no captions");
  }
  if (state.audioVoiceover) {
    lanes.push("optional narrator, trailer narrator, voiceover, announcer-style VO, or trailer VO only when the story beat, trailer format, or pitch structure calls for it");
  }
  if (state.audioScore) {
    lanes.push(`cinematic score, stings, hits, and risers that support the ${tone.label.toLowerCase()} ${genre.label.toLowerCase()} tone`);
  }
  if (state.audioMusicBed) {
    lanes.push("a restrained music bed when it helps pacing or emotion, ducked under dialogue and voiceover");
  }

  const disabled = [];
  if (!state.audioSfx) disabled.push("do not generate filler Foley, constant room hiss, or synthetic noise beds");
  if (!state.audioDialogue) disabled.push("do not invent character dialogue, murmurs, ad-libs, or off-script speech");
  if (!state.audioVoiceover) disabled.push("do not generate narrator, trailer narrator, trailer VO, offscreen announcer, announcer-style VO, or voiceover lines");
  if (!state.audioScore) disabled.push("do not generate score stings, risers, or musical hits");
  if (!state.audioMusicBed) disabled.push("do not add a continuous music bed");

  const laneText = lanes.length ? lanes.join("; ") : "only the minimum sound required by the visual action";
  return [
    `Audio direction: ${mode.label} for a ${format.label.toLowerCase()}; ${laneText}.`,
    disabled.length ? `Disabled audio lanes: ${disabled.join("; ")}.` : "",
    "Audio safety: do not vocalize timecodes, clip labels, shot labels, scene headings, camera directions, edit directions, prompt instructions, safety rules, or production phrases.",
    "Only explicit approved character dialogue may become speech; otherwise use diegetic sound, breath, footsteps, impacts, ambience, score, or music.",
    "Do not use synthetic white noise, pink noise, constant hiss washes, accidental mumbling, captions, subtitles, or visible text to represent audio.",
    "If a lane is not needed by the beat, keep it out rather than filling dead air."
  ].filter(Boolean).join(" ");
}

function imageSizeForAspect(aspectId: string): string {
  if (aspectId === "9x16" || aspectId === "4x5") return "1024x1536";
  if (aspectId === "16x9" || aspectId === "239x1") return "1536x1024";
  return "1024x1024";
}

function qualityMultiplier(quality: string): number {
  if (quality === "480p") return 0.5;
  if (quality === "1080p") return 2;
  if (quality === "4K") return 4;
  return 1;
}

function modelCreditMultiplier(modelId: string): number {
  return modelId === "seedance_2_0_mini" ? 0.65 : 1;
}

function minutes(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

function safeProjectId(value: string): string {
  const cleaned = value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return cleaned ? `director-${cleaned}` : "director-story";
}

function currentTimeLabel(): string {
  return new Date().toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

function createEmptyReferenceRegistry(message = "Reference names have not been planned yet."): ReferenceRegistry {
  return {
    characterCards: [],
    sceneReferences: [],
    status: "empty",
    message,
    updatedAt: null
  };
}

function slugForFile(value: string): string {
  const cleaned = value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  return cleaned || "asset";
}

function characterFileName(roleId: string): string {
  if (roleId === "hero") return "hero_character_card.png";
  if (roleId === "support") return "support_character_card.png";
  if (roleId === "antagonist") return "antagonist_character_card.png";
  return `${slugForFile(roleId)}_character_card.png`;
}

function sceneFileName(index: number): string {
  return `scene_${String(index + 1).padStart(3, "0")}-plate.png`;
}

function plannedStorageKey(projectId: string, kind: ReferenceKind, fileName: string): string {
  return `projects/${projectId}/assets/${kind}/planned-${fileName}`;
}

function isReadyReference(asset: ReferenceAsset): boolean {
  return Boolean(asset.storageKey && (asset.status === "generated" || asset.status === "uploaded"));
}

function countReferenceRegistry(registry: ReferenceRegistry): ReferenceCounts {
  const assets = [...registry.characterCards, ...registry.sceneReferences];
  return {
    total: assets.length,
    ready: assets.filter(isReadyReference).length,
    planned: assets.filter((asset) => asset.status === "planned" || asset.status === "generating").length,
    characterCards: registry.characterCards.length,
    sceneReferences: registry.sceneReferences.length
  };
}

function referencesRequiredForState(state: StoryState): boolean {
  return Boolean(state.imageModel || state.characterCards || state.storyboards || state.faceLock);
}

function readyReferenceKeysForChunk(registry: ReferenceRegistry, sceneId: string): string[] {
  const scenePlate = registry.sceneReferences.find((asset) => asset.sceneId === sceneId && isReadyReference(asset));
  const characterCards = registry.characterCards
    .filter(isReadyReference)
    .map((asset) => asset.storageKey);
  return [
    scenePlate?.storageKey || "",
    ...characterCards
  ].filter(Boolean).slice(0, 9);
}

function plannedReferenceKeysForChunk(registry: ReferenceRegistry, sceneId: string): string[] {
  const scenePlate = registry.sceneReferences.find((asset) => asset.sceneId === sceneId);
  return [
    scenePlate?.plannedStorageKey || "",
    ...registry.characterCards.map((asset) => asset.plannedStorageKey)
  ].filter(Boolean).slice(0, 9);
}

function characterReferenceRole(asset: ReferenceAsset): string {
  const haystack = `${asset.id} ${asset.role || ""} ${asset.label}`.toLowerCase();
  if (haystack.includes("hero") || haystack.includes("lead")) return "lead character appearance, wardrobe, face, and hero prop continuity";
  if (haystack.includes("support")) return "supporting cast appearance, wardrobe, and relationship continuity";
  if (haystack.includes("antagonist") || haystack.includes("villain")) return "antagonist or threat appearance, wardrobe, and pressure continuity";
  return "character appearance and wardrobe continuity";
}

function referenceRolesForChunk(registry: ReferenceRegistry, sceneId: string): ReferenceRole[] {
  const scenePlate = registry.sceneReferences.find((asset) => asset.sceneId === sceneId && isReadyReference(asset));
  const orderedAssets = [
    scenePlate,
    ...registry.characterCards.filter(isReadyReference)
  ].filter((asset): asset is ReferenceAsset => Boolean(asset)).slice(0, 9);

  return orderedAssets.map((asset, index) => ({
    token: `@Image${index + 1}`,
    role: asset.kind === "scene_reference"
      ? "scene plate for location, lighting, blocking geography, and background continuity"
      : characterReferenceRole(asset),
    label: asset.label,
    assetId: asset.id,
    storageKey: asset.storageKey,
    status: asset.status,
    sourceMode: asset.sourceMode
  }));
}

function buildReferenceRoleDirection(roles: ReferenceRole[]): string {
  if (!roles.length) {
    return "Seedance reference role map: no image references are attached; preserve continuity from the story blueprint and do not invent replacement identities.";
  }
  const roleMap = roles
    .map((role) => `${role.token} is ${role.role} (${role.label})`)
    .join("; ");
  return `Seedance reference role map: ${roleMap}. Use each @Image only for its assigned role; do not swap hero, support, antagonist, scene plate, key prop, wardrobe, or location identities.`;
}

function recordArray(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value)
    ? value.filter((item): item is Record<string, unknown> => Boolean(item) && typeof item === "object" && !Array.isArray(item))
    : [];
}

function textValue(value: unknown): string {
  return typeof value === "string" ? value : value == null ? "" : String(value);
}

function screenplayRoleLabel(role: ScreenplayCharacter["role"]): string {
  if (role === "lead") return "Lead character";
  if (role === "antagonist") return "Antagonist";
  if (role === "minor") return "Minor character";
  return "Supporting character";
}

function buildCharacterSpecs(state: StoryState, blueprint: Blueprint, screenplay?: Screenplay | null): CharacterSpec[] {
  if (screenplay && screenplay.characters.length) {
    const visual = findOption(visuals, state.visual);
    const era = findOption(eras, state.era);
    const look = findOption(characterLooks, state.characterLook);
    return screenplay.characters.slice(0, 6).map((character) => ({
      id: character.id,
      name: character.name,
      role: screenplayRoleLabel(character.role),
      description: [
        character.look,
        character.want ? `Wants: ${character.want}.` : "",
        character.flaw ? `Flaw: ${character.flaw}.` : "",
        character.tell ? `Tell: ${character.tell}.` : ""
      ].filter(Boolean).join(" "),
      archetype: screenplayRoleLabel(character.role),
      wardrobe: [
        character.wardrobe,
        `${look.label}. ${visual.label} palette, ${era.label} texture, consistent face, wardrobe, prop language, and silhouette.`
      ].filter(Boolean).join(" "),
      fileName: characterFileName(character.id),
      locked: false,
      sourceOfTruth: false,
      cardStatus: "planned"
    }));
  }
  const lead = findOption(leadTypes, state.leadType);
  const support = findOption(supportTypes, state.supportType);
  const antagonist = findOption(antagonistTypes, state.antagonistType);
  const arc = findOption(arcs, state.arc);
  const visual = findOption(visuals, state.visual);
  const era = findOption(eras, state.era);
  const look = findOption(characterLooks, state.characterLook);
  const baseWardrobe = `${look.label}. ${visual.label} palette, ${era.label} texture, consistent face, wardrobe, prop language, and silhouette.`;
  const specs: CharacterSpec[] = [
    {
      id: "hero",
      name: "Hero Lead",
      role: "Lead character",
      description: `Primary protagonist for ${blueprint.title}; carries the ${arc.label.toLowerCase()} arc and makes the final moral choice.`,
      archetype: lead.label,
      wardrobe: baseWardrobe,
      fileName: characterFileName("hero"),
      locked: false,
      sourceOfTruth: false,
      cardStatus: "planned"
    },
    {
      id: "support",
      name: "Supporting Lead",
      role: "Supporting character",
      description: `The closest ally or foil to the hero; challenges the premise and makes the central relationship playable on screen.`,
      archetype: support.label,
      wardrobe: baseWardrobe,
      fileName: characterFileName("support"),
      locked: false,
      sourceOfTruth: false,
      cardStatus: "planned"
    },
    {
      id: "antagonist",
      name: "Antagonist Force",
      role: "Antagonist",
      description: `The person or pressure system opposing the hero; visually readable as the source of conflict without becoming generic.`,
      archetype: antagonist.label,
      wardrobe: baseWardrobe,
      fileName: characterFileName("antagonist"),
      locked: false,
      sourceOfTruth: false,
      cardStatus: "planned"
    },
    {
      id: "witness",
      name: "World Witness",
      role: "Supporting witness",
      description: "A grounded secondary character who shows how the story world affects ordinary people.",
      archetype: "World witness",
      wardrobe: baseWardrobe,
      fileName: characterFileName("witness"),
      locked: false,
      sourceOfTruth: false,
      cardStatus: "planned"
    },
    {
      id: "wildcard",
      name: "Wildcard Turn",
      role: "Wildcard",
      description: "A volatile character who can create surprise, comedy, betrayal, or a late-story pivot.",
      archetype: "Wildcard turn",
      wardrobe: baseWardrobe,
      fileName: characterFileName("wildcard"),
      locked: false,
      sourceOfTruth: false,
      cardStatus: "planned"
    }
  ];
  return specs.slice(0, Math.max(1, Math.min(5, state.castSize)));
}

function buildSceneSpecs(state: StoryState, blueprint: Blueprint, screenplay?: Screenplay | null): SceneSpec[] {
  const visual = findOption(visuals, state.visual);
  const era = findOption(eras, state.era);
  const tone = findOption(tones, state.tone);
  if (screenplay && screenplay.scenes.length) {
    return screenplay.scenes.map((scene, index) => ({
      id: scene.id,
      sceneId: scene.id,
      heading: scene.slugline,
      slugline: scene.slugline,
      location: scene.location || `${visual.label} ${era.label} story space`,
      summary: [scene.summary, scene.turn ? `Turn: ${scene.turn}` : ""].filter(Boolean).join(" "),
      timeOfDay: scene.timeOfDay || "day",
      mood: tone.label,
      fileName: sceneFileName(index),
      locked: false,
      sourceOfTruth: false,
      referenceStatus: "planned"
    }));
  }
  return Array.from({ length: blueprint.sceneCount }, (_, index) => {
    const sceneNumber = index + 1;
    const sceneId = `scene_${String(sceneNumber).padStart(3, "0")}`;
    const act = blueprint.acts[Math.min(blueprint.acts.length - 1, Math.floor((index / blueprint.sceneCount) * blueprint.acts.length))];
    return {
      id: sceneId,
      sceneId,
      heading: `Scene ${String(sceneNumber).padStart(3, "0")} - ${act.label}`,
      slugline: `SCENE ${String(sceneNumber).padStart(3, "0")} / ${visual.label.toUpperCase()} SET`,
      location: `${visual.label} ${era.label} story space`,
      summary: `${act.text} Reference plate should support the story seed: ${blueprint.logline}`,
      timeOfDay: index % 3 === 0 ? "night" : index % 3 === 1 ? "day" : "golden hour",
      mood: tone.label,
      fileName: sceneFileName(index),
      locked: false,
      sourceOfTruth: false,
      referenceStatus: "planned"
    };
  });
}

function planReferenceRegistry(state: StoryState, blueprint: Blueprint, screenplay?: Screenplay | null): ReferenceRegistry {
  const projectId = safeProjectId(blueprint.title);
  const includeCharacterCards = state.characterCards || state.faceLock;
  const includeSceneReferences = state.imageModel || state.storyboards;
  const characterCards = includeCharacterCards
    ? buildCharacterSpecs(state, blueprint, screenplay).map((spec) => ({
        id: spec.id,
        kind: "character_card" as const,
        label: spec.name,
        role: spec.role,
        characterName: spec.name,
        fileName: spec.fileName,
        plannedStorageKey: plannedStorageKey(projectId, "character_card", spec.fileName),
        storageKey: "",
        sourceUrl: "",
        status: "planned" as const,
        provider: "planned" as const,
        sourceMode: "planned" as const,
        locked: false,
        sourceOfTruth: false
      }))
    : [];
  const sceneReferences = includeSceneReferences
    ? buildSceneSpecs(state, blueprint, screenplay).map((spec) => ({
        id: spec.id,
        kind: "scene_reference" as const,
        label: spec.heading,
        sceneId: spec.sceneId,
        sceneLabel: spec.heading,
        fileName: spec.fileName,
        plannedStorageKey: plannedStorageKey(projectId, "scene_reference", spec.fileName),
        storageKey: "",
        sourceUrl: "",
        status: "planned" as const,
        provider: "planned" as const,
        sourceMode: "planned" as const,
        locked: false,
        sourceOfTruth: false
      }))
    : [];
  const total = characterCards.length + sceneReferences.length;
  return {
    characterCards,
    sceneReferences,
    status: total ? "planned" : "empty",
    message: total
      ? "Reference names planned with Visio/Kinetix continuity conventions."
      : "No reference assets are enabled for this package.",
    updatedAt: currentTimeLabel()
  };
}

function finalizeReferenceRegistry(registry: ReferenceRegistry, warnings: string[] = []): ReferenceRegistry {
  const counts = countReferenceRegistry(registry);
  const assets = [...registry.characterCards, ...registry.sceneReferences];
  const allNotConfigured = assets.length > 0 && assets.every((asset) => asset.status === "not_configured");
  const hasFailures = assets.some((asset) => asset.status === "failed" || asset.status === "missing");
  const warningText = warnings.length ? ` ${warnings.slice(0, 2).join(" ")}` : "";
  if (!counts.total) {
    return {
      ...registry,
      status: "empty",
      message: `No reference assets are enabled for this package.${warningText}`,
      updatedAt: currentTimeLabel()
    };
  }
  if (counts.ready === counts.total) {
    return {
      ...registry,
      status: "ready",
      message: `${counts.ready} image reference${counts.ready === 1 ? "" : "s"} ready for EvoLink imageKeys.${warningText}`,
      updatedAt: currentTimeLabel()
    };
  }
  if (allNotConfigured) {
    return {
      ...registry,
      status: "not_configured",
      message: `OpenAI image generation is not configured, so planned names are ready but no storage keys were created.${warningText}`,
      updatedAt: currentTimeLabel()
    };
  }
  if (counts.ready > 0) {
    return {
      ...registry,
      status: "partial",
      message: `${counts.ready} of ${counts.total} references have storage keys. Complete the remaining references before live send.${warningText}`,
      updatedAt: currentTimeLabel()
    };
  }
  return {
    ...registry,
    status: hasFailures ? "error" : "planned",
    message: `${counts.total} planned references still need generated or uploaded storage keys before live send.${warningText}`,
    updatedAt: currentTimeLabel()
  };
}

function titleFromSeed(seed: string, genre: Option): string {
  const words = seed
    .replace(/[^a-zA-Z0-9\s-]/g, "")
    .split(/\s+/)
    .filter((word) => word.length > 3 && !["that", "with", "from", "into", "they", "their", "should"].includes(word.toLowerCase()));
  const picked = words.slice(0, 3).map((word) => word[0].toUpperCase() + word.slice(1).toLowerCase());
  return picked.length >= 2 ? picked.join(" ") : `${genre.label} Story`;
}

function storyObjectFromSeed(storyFocus: string, twist: Option): string {
  const lower = storyFocus.toLowerCase();
  if (lower.includes("package") || lower.includes("memory")) return "the package and impossible memory";
  if (lower.includes("singer") || lower.includes("song") || lower.includes("music")) return "the performance and the secret it exposes";
  if (lower.includes("heist") || lower.includes("crew")) return "the target and the plan";
  if (lower.includes("family")) return "the family bond under pressure";
  if (lower.includes("monster") || lower.includes("curse")) return "the story rule that can save or doom them";
  return `the ${twist.label.toLowerCase()} story object`;
}

function buildStoryCoherenceRules(storyObject: string): StoryCoherenceRule[] {
  return [
    {
      label: "Post-Run Review",
      contract: "Analyze the last render before any new paid test.",
      promptRule: "Before the next run, review story logic, repeated motion, odd AI physics, audio dropouts, accidental text, and continuity drift."
    },
    {
      label: "MacGuffin Lock",
      contract: `Keep ${storyObject} screen-relevant across the sequence.`,
      promptRule: `The primary story object/MacGuffin is ${storyObject}; keep it visible, handled, pursued, protected, mentioned by behavior, or clearly carried forward.`
    },
    {
      label: "One Cause/Effect",
      contract: "Every clip has one physical cause and one visible effect.",
      promptRule: "Show the mechanism before the result: no abstract flash-cut escapes, collage inserts, repeated running, or micro-cut bursts that do not change the situation."
    },
    {
      label: "Reference Roles",
      contract: "Map @Image references by role and keep those roles stable.",
      promptRule: "Name @Image1, @Image2, and later references as scene, lead, support, antagonist, or key prop roles; never swap identities."
    },
    {
      label: "Threat Carry",
      contract: "The antagonist or pressure remains visible or deliberately carried forward.",
      promptRule: "If a threat appears, show it blocked, dodged, disabled, escaped, or actively pursuing into the next beat instead of vanishing."
    },
    {
      label: "Text Lock",
      contract: "No unapproved visible words, prop numbers, labels, or signage.",
      promptRule: "Avoid readable signs, fake UI, prop numbers, random website text, subtitles, captions, lower thirds, logos, watermarks, or garbled signage."
    },
    {
      label: "Audio Focus",
      contract: "One audible gag, dialogue idea, or VO idea per short beat.",
      promptRule: "Dialogue, score, music bed, and VO are allowed when needed, but do not stack ad-lib chatter, production terms, or filler noises."
    }
  ];
}

function buildBlockingPlanForBeat(
  beat: Omit<StoryLogicBeat, "blockingPlan">,
  storyObject: string,
  protagonist: string,
  pressure: string,
  index: number
): BlockingPlan {
  const primary = `${protagonist} lead`;
  const secondary = index === 1 ? `${pressure} pressure` : index === 2 ? "reaction witness" : "support or obstacle";
  const objectMark = index === 0
    ? `${storyObject} enters center-right foreground`
    : index === 1
      ? `${storyObject} is contested at the route or barrier`
      : `${storyObject} resolves in the final readable frame`;
  return {
    characterMarks: [
      {
        character: primary,
        framePosition: index === 2 ? "center foreground" : "camera-left third",
        depth: "foreground",
        bodyOrientation: "three-quarter toward the active story object",
        eyelineTarget: storyObject,
        action: beat.visibleAction
      },
      {
        character: secondary,
        framePosition: index === 1 ? "camera-right third blocking the exit" : "camera-right reaction lane",
        depth: index === 1 ? "midground" : "background-to-midground",
        bodyOrientation: "angled toward the lead",
        eyelineTarget: primary,
        action: index === 1 ? "forces the route choice to stay visible" : "keeps the consequence readable"
      }
    ],
    objectMark,
    movementPath: index === 0
      ? "lead moves left-to-right into the decision mark"
      : index === 1
        ? "lead crosses the blocked route, changes tactic, then exits frame-right"
        : "lead settles into center frame as the payoff lands",
    cameraAxis: "keep lead camera-left looking camera-right until the final payoff centers them",
    screenDirection: "left-to-right progress unless the beat intentionally reverses power",
    startFrame: beat.threat,
    endFrame: beat.consequence,
    storyChange: beat.payoff,
    continuityRisk: "Do not reset positions, swap eyelines, hide the story object, or skip the visible route between clips"
  };
}

function blockingPromptDirection(blocking: BlockingPlan): string {
  const marks = blocking.characterMarks
    .map((mark) => `${mark.character} ${mark.framePosition}, ${mark.depth}, ${mark.action}`)
    .join("; ");
  return [
    `Blocking map: ${marks}.`,
    `Object mark: ${blocking.objectMark}.`,
    `Movement path: ${blocking.movementPath}.`,
    `Camera axis: ${blocking.cameraAxis}.`,
    `End frame: ${blocking.endFrame}.`
  ].join(" ");
}

function buildStoryLogicBeats(
  state: StoryState,
  storyFocus: string,
  genre: Option,
  structure: Option,
  arc: Option,
  stakesOption: Option,
  twist: Option
): StoryLogicBeat[] {
  const storyObject = storyObjectFromSeed(storyFocus, twist);
  const protagonist = arc.label.toLowerCase();
  const pressure = stakesOption.label.toLowerCase();
  const format = findOption(formats, state.format);
  const firstTurn = minutes(Math.max(8, Math.round(state.runtime * 0.2)));
  const secondTurn = minutes(Math.max(18, Math.round(state.runtime * 0.72)));
  const runtimeLabel = minutes(state.runtime);
  const bridgeChoices = "doorway, stairs, vehicle, crowd, ally, barrier, tool, revealed route, or direct confrontation";

  const baseBeats: Array<Omit<StoryLogicBeat, "blockingPlan">> = [
    {
      label: "Cause Chain",
      time: `0:00-${firstTurn}`,
      purpose: `Open in action and prove why the ${protagonist} lead cannot ignore ${storyObject}.`,
      threat: `${pressure} pressure enters the frame and interrupts the lead's normal plan.`,
      choice: "The lead chooses risk over avoidance, making the story move because of a decision.",
      visibleAction: `Show the lead physically acquiring, hiding, protecting, or decoding ${storyObject}.`,
      consequence: "The opposing force notices the choice, the clock starts, and safety is gone.",
      locationBridge: `End the beat with a visible next route: ${bridgeChoices}.`,
      payoff: `Set up the object, skill, or rule that must pay off before the ${format.label.toLowerCase()} ends.`,
      promptRule: "No abstract flash-cut escape, no unexplained relocation, no meta filmmaking phrase as speech, and no random cool image unless it changes the situation."
    },
    {
      label: "Pressure And Escape",
      time: `${firstTurn}-${secondTurn}`,
      purpose: `${structure.label} escalation forces the lead to solve a screen-visible problem under ${genre.label.toLowerCase()} pressure.`,
      threat: "The antagonist, system, environment, or deadline corners the lead in a specific place.",
      choice: "The lead must choose a tactic: bargain, distract, outrun, outthink, sacrifice, or expose truth.",
      visibleAction: "If a threat blocks the lead, show the exact escape mechanism before changing locations.",
      consequence: "The escape creates a new cost, injury, clue, betrayal, or worse deadline.",
      locationBridge: "Carry the last shot's geography into the next clip with a visible exit, pursuit, open vehicle door, elevator, hallway, or match-on-action.",
      payoff: "Use one earlier setup as a practical tool, clue, emotional lever, or weakness.",
      promptRule: "Threats introduced on screen must be dodged, disabled, escaped, or clearly carried forward into the next beat; no one passes through solid objects."
    },
    {
      label: "Payoff And New State",
      time: `${secondTurn}-${runtimeLabel}`,
      purpose: `${twist.label} resolves or reframes the central question through a final visible choice.`,
      threat: "The last obstacle forces the lead to reveal what they value more than safety.",
      choice: "The lead chooses the cost they are willing to pay and acts on it.",
      visibleAction: `Show the payoff of ${storyObject} through behavior, prop use, location change, or a character reaction.`,
      consequence: "The world, relationship, or mission ends in a visibly different state than where it began.",
      locationBridge: "The final transition should answer how the lead got there, not skip over the decisive move; vehicle boarding must show an open entry path.",
      payoff: "The last visual answers the opening question and leaves a trailer-safe hook if the story continues.",
      promptRule: "The ending must pay off an earlier setup; avoid mystery that only exists because a needed action was omitted or physics was cheated."
    }
  ];
  return baseBeats.map((beat, index) => ({
    ...beat,
    blockingPlan: buildBlockingPlanForBeat(beat, storyObject, protagonist, pressure, index)
  }));
}

function logicBeatForChunk(blueprint: Blueprint, index: number, totalChunks: number): StoryLogicBeat {
  return blueprint.logicBeats[Math.min(
    blueprint.logicBeats.length - 1,
    Math.floor((index / totalChunks) * blueprint.logicBeats.length)
  )];
}

function buildStoryLogicDirection(beat: StoryLogicBeat): string {
  return [
    `Story logic contract (${beat.label}): ${beat.purpose}`,
    `Threat/choice/action/consequence: ${beat.threat} ${beat.choice} ${beat.visibleAction} ${beat.consequence}`,
    blockingPromptDirection(beat.blockingPlan),
    `Continuity bridge: ${beat.locationBridge}`,
    `Payoff: ${beat.payoff}`,
    beat.promptRule
  ].join(" ");
}

function buildStoryCoherenceDirection(blueprint: Blueprint, beat: StoryLogicBeat, chunkIndex: number, totalChunks: number): string {
  return [
    `Coherence lock for clip ${chunkIndex + 1}/${totalChunks}: the primary story object/MacGuffin is ${blueprint.storyObject}; keep it visible, held, pursued, protected, or clearly carried forward.`,
    "One physical cause must create one visible effect in this clip; show the mechanism before any escape, reveal, or location change.",
    `Blocking continuity: ${beat.blockingPlan.cameraAxis} ${beat.blockingPlan.screenDirection}. Preserve the object mark: ${beat.blockingPlan.objectMark}.`,
    `Threat carry-forward: ${beat.threat} Keep the pressure visible, audible, or deliberately unresolved into the next beat.`,
    "Visible text lock: no random readable signage, prop numbers, fake UI, subtitles, captions, lower thirds, logos, watermarks, or garbled text.",
    "Audio focus: use at most one audible gag, dialogue idea, or VO idea in this short beat; no ad-lib filler or production terms as speech."
  ].join(" ");
}

function buildPhysicalActionDirection(): string {
  return [
    "Physical action lock: bodies, vehicles, walls, windows, doors, and trains are solid.",
    "A character may only enter a vehicle through a visibly open doorway, hatch, window, or platform gap with a clear hand grip and foot plant.",
    "If the vehicle is moving too fast, show the character grabbing an exterior handle or staying on the platform; do not pass through the vehicle shell, closed doors, glass, or metal.",
    "Every jump, fall, slide, collision, door entry, elevator entry, train entry, tram entry, or vehicle exit needs visible contact points and plausible body mechanics."
  ].join(" ");
}

function buildPostRunReviewPolicy() {
  return {
    requiredBeforeNextRun: true,
    checks: [
      "Watch the prior master before a new run and record keep/fix/rerender notes.",
      "Verify every clip has a clear cause, visible action, consequence, and location bridge.",
      "Flag repeated motion energy, repeated running inserts, missing escape mechanics, or action that ignores solid objects.",
      "Check antagonist/threat visibility and whether the primary story object is still trackable.",
      "Review accidental readable text, fake UI, captions, logos, watermarks, or prompt words spoken aloud.",
      "Run an ASR/listen pass for unintended dialogue, production phrases, dropped audio, or missing tail fade."
    ],
    finalAssembly: {
      loudnessTarget: "-18 LUFS integrated phone-friendly master, true peak <= -1.5 dBTP",
      soundBed: "No synthetic white-noise bed, pink-noise bed, or constant hiss wash; use natural Foley, breath, footsteps, impacts, ambience, dialogue, VO, score, or music bed only when motivated.",
      tail: "Add a short musical or ambience tail fade so no scene cuts off abruptly.",
      transcript: "ASR or human transcript pass must confirm no prompt directions, edit terms, timecodes, or unapproved lines are audible."
    }
  };
}

function buildRenderPlan(state: StoryState, blueprint: Blueprint): RenderPlan {
  const model = findOption(evolinkModels, state.evolinkModel);
  const videoResolution = normalizeVideoResolution(state.evolinkModel, state.videoResolution);
  const generationResolution = resolutionLabel(videoResolution);
  const providerQuality = providerQualityForResolution(videoResolution);
  const upscaleTarget = state.autoUpscale ? upscaleTargetForResolution(videoResolution) : generationResolution;
  const deliveryResolution = state.autoUpscale ? `${generationResolution} to ${upscaleTarget}` : `${generationResolution} native`;
  const averageChunkSeconds = Math.max(4, Math.min(15, Math.ceil(blueprint.runtimeSeconds / blueprint.chunkCount)));
  const estimatedCredits = Math.round(
    blueprint.runtimeSeconds *
    10 *
    qualityMultiplier(providerQuality) *
    modelCreditMultiplier(state.evolinkModel)
  );
  const aspectRatio = providerAspectRatio(state.aspect);
  const providerModel = providerModelForEvolink(state.evolinkModel);
  const audioSummary = audioModeSummary(state);

  return {
    providerModel,
    modelLabel: model.label,
    aspectRatio,
    generationResolution,
    providerQuality,
    deliveryResolution,
    averageChunkSeconds,
    estimatedCredits,
    sceneCount: blueprint.sceneCount,
    chunkCount: blueprint.chunkCount,
    payloadLines: [
      { label: "Endpoint", value: "/api/render/start" },
      { label: "Provider model", value: providerModel },
      { label: "Quality", value: providerQuality },
      { label: "Aspect", value: aspectRatio },
      { label: "Chunks", value: `${blueprint.chunkCount} clips, about ${averageChunkSeconds}s each` },
      { label: "Motion", value: chunkProfileSummary(state) },
      { label: "Chaining", value: state.continuityChain ? "Sequential — each clip references the previous clip's footage" : "Parallel — all clips start at once" },
      { label: "Voices", value: state.seedAudioVoices ? "Seed Audio 1.0 generates lines, clips lip-sync to @audio1" : "Seedance native voices" },
      { label: "Audio", value: audioSummary },
      { label: "Delivery", value: deliveryResolution }
    ]
  };
}

function compactShotAudioDirection(state: StoryState, shot: ScreenplayShot, spoken: boolean): string {
  if (!shouldGenerateAudio(state)) {
    return "Audio: silent planning pass — do not generate dialogue, voiceover, score, or music.";
  }
  const parts: string[] = [];
  if (shot.sound) parts.push(`Sound design: ${shot.sound}.`);
  else if (state.audioSfx) parts.push("Sound design: natural motivated foley and room tone only.");
  if (shot.dialogue && !spoken) {
    parts.push("No spoken words in this clip; play the beat through physical performance and reaction.");
  }
  if (!state.audioVoiceover) parts.push("No narrator or voiceover.");
  if (!state.audioScore && !state.audioMusicBed) parts.push("No score or music bed.");
  else parts.push("Score continuity: continue the film's single musical identity; never restart or switch themes mid-film.");
  parts.push("Never vocalize production terms, clip labels, or prompt text; no white-noise or hiss beds.");
  return `Audio: ${parts.join(" ")}`;
}

function buildScreenplayShotPrompt(
  state: StoryState,
  screenplay: Screenplay,
  shot: ScreenplayShot,
  previousShot: ScreenplayShot | null,
  index: number,
  totalChunks: number,
  plan: RenderPlan,
  referenceRoleDirection: string
): string {
  const visual = findOption(visuals, state.visual);
  const era = findOption(eras, state.era);
  const director = findOption(directors, state.director);
  const scene = screenplay.scenes.find((item) => item.id === shot.sceneId);
  const spoken = spokenDialogueActive(state);
  const speaker = shot.dialogue
    ? screenplay.characters.find((character) => character.name === shot.dialogue?.character || character.id === shot.dialogue?.character)
    : undefined;
  const dialogueDirection = shot.dialogue && spoken
    ? `${(shot.dialogue.character || "The character").toUpperCase()} speaks exactly this line and no other words: "${shot.dialogue.line}"${shot.dialogue.delivery ? ` Delivery: ${shot.dialogue.delivery}.` : ""}${speaker?.voice ? ` Voice: ${speaker.voice.slice(0, 120)} — keep this exact voice identity consistent with this character's other clips.` : ""} Keep the mouth readable and lip sync natural; the spoken words never appear as visible text.`
    : "";
  const actionLower = shot.action.toLowerCase();
  const propContinuity = screenplay.props
    .filter((prop) => prop.name && prop.continuity && actionLower.includes(prop.name.toLowerCase()))
    .slice(0, 2)
    .map((prop) => `${prop.name}: ${prop.continuity.slice(0, 100)}`)
    .join("; ");
  const continuityEntry = (previousShot?.exitState || shot.entryState).slice(0, 200);
  const continuityDirection = [
    continuityEntry ? `Continues directly from: ${continuityEntry}.` : "",
    shot.exitState ? `This clip must end with: ${shot.exitState.slice(0, 200)}.` : "",
    shot.screenDirection ? `Screen direction: ${shot.screenDirection}.` : ""
  ].filter(Boolean).join(" ");
  // Includes the worker's guard phrase ("dialogue and script lines are audio/performance only")
  // so guardedRenderPrompt keeps this compact, script-specific prompt instead of appending
  // the long generic guard and truncating the story content. Budget-aware: the worker hard-caps
  // prompts at 1800 characters, so the shot action flexes to keep the safety tail intact.
  const fixedParts = [
    dialogueDirection,
    compactShotAudioDirection(state, shot, spoken),
    continuityDirection ? `Continuity: ${continuityDirection}` : "",
    propContinuity ? `Prop continuity: ${propContinuity}.` : "",
    referenceRoleDirection,
    `Style: ${visual.label}, ${era.label} texture, ${director.label} camera energy.${screenplay.world.look ? ` World look: ${screenplay.world.look.slice(0, 160)}.` : ""}`,
    `Frame for ${plan.aspectRatio}. Dialogue and script lines are audio/performance only; never display their words on screen.`,
    "Solid physics with visible contact points; identities, wardrobe, and props stay consistent with their references; no readable text, captions, subtitles, signage, fake UI, logos, or watermarks; no celebrity lookalikes."
  ].filter(Boolean);
  const header = `Clip ${index + 1} of ${totalChunks} — ${scene ? scene.slugline : "continuous scene"}.${shot.shotType ? ` ${shot.shotType}.` : ""}`;
  const fixedLength = header.length + fixedParts.join(" ").length + 2;
  const actionBudget = Math.max(260, 1780 - fixedLength);
  return [header, shot.action.slice(0, actionBudget), ...fixedParts].join(" ");
}

function buildRenderChunks(state: StoryState, blueprint: Blueprint, plan: RenderPlan, references: ReferenceRegistry, screenplay?: Screenplay | null) {
  if (screenplay && screenplay.shots.length) {
    const generateAudio = shouldGenerateAudio(state);
    const totalChunks = Math.min(screenplay.shots.length, 80);
    const characterNames = new Map(screenplay.characters.map((character) => [character.id, character.name]));
    const charactersByKey = new Map<string, ScreenplayCharacter>();
    screenplay.characters.forEach((character) => {
      charactersByKey.set(character.id, character);
      charactersByKey.set(character.name, character);
    });
    const seedAudioActive = state.seedAudioVoices && spokenDialogueActive(state);
    return screenplay.shots.slice(0, totalChunks).map((shot, index) => {
      const previousShot = index > 0 ? screenplay.shots[index - 1] : null;
      const logicBeat = logicBeatForChunk(blueprint, index, totalChunks);
      const referenceRoles = referenceRolesForChunk(references, shot.sceneId);
      const referenceRoleDirection = buildReferenceRoleDirection(referenceRoles);
      const scene = screenplay.scenes.find((item) => item.id === shot.sceneId);
      return {
        sceneId: shot.sceneId,
        chunkId: shot.id || `clip_${String(index + 1).padStart(3, "0")}`,
        label: `${scene ? scene.slugline : `Scene ${index + 1}`} — ${shot.purpose || shot.shotType || `shot ${index + 1}`}`.slice(0, 160),
        durationSeconds: Math.max(4, Math.min(15, Math.round(shot.durationSeconds) || 8)),
        model: plan.providerModel,
        generateAudio,
        storyLogic: logicBeat,
        blockingPlan: logicBeat.blockingPlan,
        storyObject: blueprint.storyObject,
        coherenceRules: blueprint.coherenceRules,
        referenceRoles,
        imageKeys: readyReferenceKeysForChunk(references, shot.sceneId),
        plannedReferenceKeys: plannedReferenceKeysForChunk(references, shot.sceneId),
        referenceIds: [
          ...references.sceneReferences.filter((asset) => asset.sceneId === shot.sceneId).map((asset) => asset.id),
          ...references.characterCards.map((asset) => asset.id)
        ],
        audioKeys: [],
        videoKeys: [],
        seedAudio: seedAudioActive && shot.dialogue && shot.dialogue.line ? {
          character: characterNames.get(shot.dialogue.character) || shot.dialogue.character,
          line: shot.dialogue.line,
          delivery: shot.dialogue.delivery,
          voice: charactersByKey.get(shot.dialogue.character)?.voice || ""
        } : undefined,
        screenplayShot: {
          id: shot.id,
          sceneId: shot.sceneId,
          purpose: shot.purpose,
          dialogueCharacter: shot.dialogue ? (characterNames.get(shot.dialogue.character) || shot.dialogue.character) : "",
          dialogueLine: shot.dialogue?.line || "",
          entryState: previousShot?.exitState || shot.entryState,
          exitState: shot.exitState
        },
        prompt: buildScreenplayShotPrompt(state, screenplay, shot, previousShot, index, totalChunks, plan, referenceRoleDirection)
      };
    });
  }
  const format = findOption(formats, state.format);
  const genre = findOption(genres, state.genre);
  const tone = findOption(tones, state.tone);
  const visual = findOption(visuals, state.visual);
  const director = findOption(directors, state.director);
  const era = findOption(eras, state.era);
  const totalChunks = Math.min(blueprint.chunkCount, 80);
  const finalChunkSeconds = Math.max(4, Math.min(15, Math.ceil(state.runtime / totalChunks)));
  const generateAudio = shouldGenerateAudio(state);
  const audioDirection = buildAudioDirection(state, format, genre, tone);
  const motionDirection = buildMotionDirection(state);
  const physicalActionDirection = buildPhysicalActionDirection();

  return Array.from({ length: totalChunks }, (_, index) => {
    const sceneNumber = Math.floor((index / totalChunks) * blueprint.sceneCount) + 1;
    const sceneId = `scene_${String(sceneNumber).padStart(3, "0")}`;
    const beat = blueprint.acts[Math.min(blueprint.acts.length - 1, Math.floor((index / totalChunks) * blueprint.acts.length))];
    const logicBeat = logicBeatForChunk(blueprint, index, totalChunks);
    const storyLogicDirection = buildStoryLogicDirection(logicBeat);
    const storyCoherenceDirection = buildStoryCoherenceDirection(blueprint, logicBeat, index, totalChunks);
    const referenceRoles = referenceRolesForChunk(references, sceneId);
    const referenceRoleDirection = buildReferenceRoleDirection(referenceRoles);
    return {
      sceneId,
      chunkId: `clip_${String(index + 1).padStart(3, "0")}`,
      label: `${beat.label} ${index + 1}: ${logicBeat.label}`,
      durationSeconds: finalChunkSeconds,
      model: plan.providerModel,
      generateAudio,
      storyLogic: logicBeat,
      blockingPlan: logicBeat.blockingPlan,
      storyObject: blueprint.storyObject,
      coherenceRules: blueprint.coherenceRules,
      referenceRoles,
      imageKeys: readyReferenceKeysForChunk(references, sceneId),
      plannedReferenceKeys: plannedReferenceKeysForChunk(references, sceneId),
      referenceIds: [
        ...references.sceneReferences.filter((asset) => asset.sceneId === sceneId).map((asset) => asset.id),
        ...references.characterCards.map((asset) => asset.id)
      ],
      audioKeys: [],
      videoKeys: [],
      prompt: [
        blueprint.logline,
        `Create clip ${index + 1} of ${totalChunks} for a ${tone.label.toLowerCase()} ${genre.label.toLowerCase()} ${findOption(formats, state.format).label.toLowerCase()}.`,
        `Visual language: ${visual.label}, ${era.label}, ${director.label}.`,
        referenceRoleDirection,
        storyLogicDirection,
        storyCoherenceDirection,
        blockingPromptDirection(logicBeat.blockingPlan),
        physicalActionDirection,
        motionDirection,
        audioDirection,
        `Frame for ${plan.aspectRatio}; keep character identity stable, motion readable, no captions, no subtitles, no random readable signage, no prop numbers, no fake UI text, no logos, no watermarks.`
      ].join(" ")
    };
  });
}

function buildRenderPayload(
  state: StoryState,
  blueprint: Blueprint,
  plan: RenderPlan,
  gates: RenderGates,
  approvedCapCredits: number,
  references: ReferenceRegistry,
  screenplay?: Screenplay | null
) {
  const chunks = buildRenderChunks(state, blueprint, plan, references, screenplay);
  return {
    projectId: safeProjectId(blueprint.title),
    title: blueprint.title,
    quality: plan.providerQuality,
    aspectRatio: plan.aspectRatio,
    approvedCapCredits,
    gates,
    generateAudio: shouldGenerateAudio(state),
    continuityChaining: state.continuityChain,
    screenplay: screenplay && screenplay.shots.length ? {
      title: screenplay.title,
      logline: screenplay.logline,
      theme: screenplay.theme,
      generatedAt: screenplay.generatedAt,
      spokenDialogue: spokenDialogueActive(state),
      characters: screenplay.characters.map((character) => ({
        id: character.id,
        name: character.name,
        role: character.role,
        look: character.look,
        wardrobe: character.wardrobe
      })),
      props: screenplay.props,
      sceneCount: screenplay.scenes.length,
      shotCount: screenplay.shots.length,
      openingImage: screenplay.openingImage,
      finalImage: screenplay.finalImage
    } : null,
    scriptHardening: {
      sources: blueprint.skillSources,
      beats: blueprint.logicBeats,
      blockingPlans: blueprint.logicBeats.map((beat) => ({
        beat: beat.label,
        time: beat.time,
        ...beat.blockingPlan
      })),
      storyObject: blueprint.storyObject,
      coherenceRules: blueprint.coherenceRules,
      rules: [
        "Threat -> choice -> visible action -> consequence -> location bridge -> payoff.",
        "One physical cause must create one visible effect in every clip.",
        "The primary story object/MacGuffin stays visible, handled, pursued, protected, or clearly carried forward.",
        "No unexplained teleports, glow-cut escapes, or location jumps without a visible bridge.",
        "Every threat introduced is resolved on screen or carried forward deliberately.",
        "Seedance image references must be mapped as @Image1, @Image2, and later role locks before generation.",
        "No random readable signage, fake UI, prop numbers, subtitles, captions, lower thirds, or garbled text.",
        "Use at most one audible gag, dialogue idea, or VO idea per short beat.",
        "Do not vocalize prompt directions, timecodes, scene labels, edit terms, or production phrases.",
        "Bodies and vehicles are solid; boarding a train, tram, elevator, car, or aircraft requires a visible open entry path and contact points."
      ],
      postRunReviewPolicy: buildPostRunReviewPolicy()
    },
    referenceManifest: {
      required: referencesRequiredForState(state),
      status: references.status,
      updatedAt: references.updatedAt,
      characterCards: references.characterCards,
      sceneReferences: references.sceneReferences
    },
    chunks,
    scenes: chunks
  };
}

async function apiPost<T>(path: string, payload: Record<string, unknown>): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload)
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message = typeof data.message === "string" ? data.message : `Request failed with ${response.status}`;
    throw new Error(message);
  }
  return data as T;
}

async function apiGet<T>(path: string): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`);
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message = typeof data.message === "string" ? data.message : `Request failed with ${response.status}`;
    throw new Error(message);
  }
  return data as T;
}

async function apiFormPost<T>(path: string, formData: FormData): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    body: formData
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.ok === false) {
    const message = typeof data.message === "string" ? data.message : `Request failed with ${response.status}`;
    throw new Error(message);
  }
  return data as T;
}

function allRenderGatesApproved(gates: RenderGates): boolean {
  return renderGateOptions.every((gate) => gates[gate.id]);
}

function buildActsFromScreenplay(screenplay: Screenplay): Array<{ label: string; time: string; text: string }> {
  const scenes = screenplay.scenes;
  const total = screenplay.runtimeSeconds || 60;
  const labels = ["Opening Hook", "Escalation", "Final Payoff"];
  const bounds = [0, Math.round(total * 0.2), Math.round(total * 0.72), total];
  return labels.map((label, index) => {
    const from = Math.floor((index / labels.length) * scenes.length);
    const to = Math.max(from + 1, Math.floor(((index + 1) / labels.length) * scenes.length));
    const text = scenes.slice(from, to).map((scene) => scene.summary).join(" ").slice(0, 220) || label;
    return { label, time: `${minutes(bounds[index])}-${minutes(bounds[index + 1])}`, text };
  });
}

function buildBlueprint(state: StoryState, screenplay?: Screenplay | null): Blueprint {
  const format = findOption(formats, state.format);
  const projectMode = findOption(projectModes, state.projectMode);
  const sourceProject = findOption(sourceProjects, state.sourceProject);
  const aspect = findOption(aspects, state.aspect);
  const genre = findOption(genres, state.genre);
  const tone = findOption(tones, state.tone);
  const structure = findOption(structures, state.structure);
  const arc = findOption(arcs, state.arc);
  const stakesOption = findOption(stakes, state.stakes);
  const twist = findOption(twists, state.twist);
  const era = findOption(eras, state.era);
  const visual = findOption(visuals, state.visual);
  const director = findOption(directors, state.director);
  const lead = findOption(leadTypes, state.leadType);
  const support = findOption(supportTypes, state.supportType);
  const antagonist = findOption(antagonistTypes, state.antagonistType);
  const characterLook = findOption(characterLooks, state.characterLook);
  const evolinkModel = findOption(evolinkModels, state.evolinkModel);
  const videoResolution = normalizeVideoResolution(state.evolinkModel, state.videoResolution);
  const generationResolution = resolutionLabel(videoResolution);
  const upscaleTarget = state.autoUpscale ? upscaleTargetForResolution(videoResolution) : generationResolution;
  const deliveryResolution = state.autoUpscale
    ? `${generationResolution} generation, auto upscale to ${upscaleTarget}`
    : `${generationResolution} native delivery`;
  const seed = state.seed.trim();
  const hasScript = Boolean(screenplay && screenplay.shots.length);
  const runtimeSeconds = hasScript && screenplay ? screenplay.runtimeSeconds : state.runtime;
  const runtimeLabel = minutes(runtimeSeconds);
  const sceneCount = hasScript && screenplay ? screenplay.scenes.length : plannedSceneCount(state);
  const chunkCount = hasScript && screenplay ? Math.min(80, screenplay.shots.length) : Math.min(80, plannedChunkCount(state));
  const protagonist = arc.label.toLowerCase();
  const storyFocus = seed || `A ${protagonist} is forced through a ${genre.label.toLowerCase()} story with ${stakesOption.label.toLowerCase()} stakes.`;
  const storyObject = hasScript && screenplay
    ? (screenplay.props[0]?.name || screenplay.theme || storyObjectFromSeed(storyFocus, twist))
    : storyObjectFromSeed(storyFocus, twist);
  const logicBeats = buildStoryLogicBeats(state, storyFocus, genre, structure, arc, stakesOption, twist);
  const coherenceRules = buildStoryCoherenceRules(storyObject);

  return {
    title: hasScript && screenplay ? screenplay.title : titleFromSeed(storyFocus, genre),
    logline: hasScript && screenplay && screenplay.logline
      ? screenplay.logline
      : `${storyFocus.replace(/\.$/, "")}. Built as a ${tone.label.toLowerCase()} ${format.label.toLowerCase()} with ${visual.label.toLowerCase()} visuals, ${era.label.toLowerCase()} texture, and a ${twist.label.toLowerCase()} payoff.`,
    storyObject,
    acts: hasScript && screenplay ? buildActsFromScreenplay(screenplay) : [
      {
        label: "Opening Hook",
        time: `0:00-${minutes(Math.max(12, Math.round(state.runtime * 0.2)))}`,
        text: `${genre.label} premise lands fast; ${stakesOption.label.toLowerCase()} pressure enters before the first turn.`
      },
      {
        label: structure.label,
        time: `${minutes(Math.round(state.runtime * 0.2))}-${minutes(Math.round(state.runtime * 0.72))}`,
        text: `${arc.label} arc escalates through ${state.castSize} key character lanes and a clean midpoint reversal.`
      },
      {
        label: "Final Payoff",
        time: `${minutes(Math.round(state.runtime * 0.72))}-${runtimeLabel}`,
        text: `${twist.label} reframes the story, then leaves a final image designed for video.`
      }
    ],
    logicBeats,
    coherenceRules,
    skillSources: hasScript ? [kinetixScriptSkillSource, ...scriptSkillSources] : scriptSkillSources,
    characters: hasScript && screenplay
      ? screenplay.characters.slice(0, 5).map((character) => `${character.name} (${screenplayRoleLabel(character.role).toLowerCase()})`)
      : [
        `${lead.label} lead`,
        `${support.label} support`,
        `${antagonist.label} antagonist`,
        state.castSize > 3 ? "World witness" : "Visual foil"
      ].slice(0, state.castSize),
    stages: [
      {
        label: "Project Continuity",
        detail: state.projectMode === "new_story"
          ? "Fresh story setup"
          : `${projectMode.label} from ${sourceProject.label}`,
        ready: state.projectMode !== "new_story"
      },
      {
        label: "Screenplay",
        detail: hasScript && screenplay
          ? `"${screenplay.title}" — ${screenplay.scenes.length} scenes, ${screenplay.shots.length} shots by the script skill`
          : "Template beats only — write the screenplay for pro-level scripting",
        ready: hasScript
      },
      { label: "Story Spine", detail: `${structure.label} beat sheet`, ready: true },
      { label: "Script Logic Pass", detail: "Threat -> choice -> visible action -> consequence -> bridge", ready: true },
      { label: "Story Coherence Gate", detail: "MacGuffin, one cause/effect, reference roles, threat carry, text lock", ready: true },
      { label: "Blocking System", detail: "Character marks, object marks, camera axis, movement path, end-frame state", ready: true },
      { label: "Skill Scan", detail: "Claude story skills, AI storyboard skill, and Kinetix production-agent rules", ready: true },
      { label: "Character Cards", detail: `${characterLook.label}, ${state.characterDepth}/5 depth`, ready: state.characterCards },
      { label: "ChatGPT Image 2", detail: state.imageModel ? "Lead, support, antagonist, and world keyframes" : "Manual image references", ready: state.imageModel },
      { label: "Storyboards", detail: state.storyboards ? `${sceneCount} scenes mapped` : "Optional storyboard skip", ready: state.storyboards },
      { label: "Sound Plan", detail: audioModeSummary(state), ready: shouldGenerateAudio(state) },
      { label: "Clip Strategy", detail: chunkProfileSummary(state), ready: true },
      { label: "Video Plan", detail: `${chunkCount} render chunks for ${aspect.label} at ${generationResolution}`, ready: true },
      { label: "Post-Run QC", detail: "-18 LUFS target, no white-noise bed, tail fade, ASR/listen pass", ready: state.renderPackage },
      { label: "EvoLink Model", detail: `${evolinkModel.label} selected`, ready: state.renderPackage },
      { label: "Delivery", detail: state.renderPackage ? deliveryResolution : "Plan-only export", ready: state.renderPackage }
    ],
    runtimeLabel,
    runtimeSeconds,
    sceneCount,
    chunkCount,
    outputShape: `${format.label} · ${aspect.label} · ${evolinkModel.label} · ${deliveryResolution} · ${chunkProfileSummary(state)} · ${director.label}`
  };
}

function Icon({ name }: { name: string }) {
  const common = { fill: "none", stroke: "currentColor", strokeLinecap: "round" as const, strokeLinejoin: "round" as const, strokeWidth: 1.8 };
  return (
    <svg className="icon" viewBox="0 0 24 24" aria-hidden="true">
      {name === "build" ? (
        <>
          <path {...common} d="M4 6h16M4 12h16M4 18h10" />
          <path {...common} d="m16 17 2 2 4-5" />
        </>
      ) : name === "characters" ? (
        <>
          <path {...common} d="M8 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM3 21a5 5 0 0 1 10 0" />
          <path {...common} d="M17 10a3 3 0 1 0 0-6M15 15a4.5 4.5 0 0 1 6 4.2V21" />
        </>
      ) : name === "visual" ? (
        <>
          <path {...common} d="M4 5h6v6H4zM14 5h6v6h-6zM4 15h6v4H4zM14 15h6v4h-6z" />
        </>
      ) : name === "arc" ? (
        <path {...common} d="M3 17c4-10 7-10 10 0 2 6 5 6 8-4" />
      ) : name === "script" ? (
        <>
          <path {...common} d="M6 3h9l4 4v14H6z" />
          <path {...common} d="M15 3v4h4M9 11h7M9 15h7M9 19h4" />
        </>
      ) : name === "pipeline" ? (
        <>
          <path {...common} d="M5 6h4v4H5zM15 6h4v4h-4zM5 14h4v4H5zM15 14h4v4h-4z" />
          <path {...common} d="M9 8h6M9 16h6M17 10v4M7 10v4" />
        </>
      ) : name === "review" ? (
        <>
          <path {...common} d="M4 5h16v12H4z" />
          <path {...common} d="m10 9 4 2-4 2V9ZM9 21h6" />
        </>
      ) : name === "spark" ? (
        <path {...common} d="m12 3 1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8L12 3ZM18 16l.8 2.2L21 19l-2.2.8L18 22l-.8-2.2L15 19l2.2-.8L18 16Z" />
      ) : name === "play" ? (
        <>
          <rect {...common} x="3" y="4" width="18" height="16" rx="2" />
          <path {...common} d="m10 9 5 3-5 3V9Z" />
        </>
      ) : name === "ratio" ? (
        <>
          <rect {...common} x="4" y="6" width="16" height="12" rx="2" />
          <path {...common} d="M8 10h8M8 14h4" />
        </>
      ) : name === "export" ? (
        <>
          <path {...common} d="M12 3v12m0 0 4-4m-4 4-4-4" />
          <path {...common} d="M5 17v3h14v-3" />
        </>
      ) : (
        <circle {...common} cx="12" cy="12" r="8" />
      )}
    </svg>
  );
}

function Sidebar({ active, onActive }: { active: string; onActive: (id: string) => void }) {
  const nav = [
    ["build", "Build"],
    ["script", "Screenplay"],
    ["characters", "Characters"],
    ["visual", "Visual DNA"],
    ["arc", "Story Arc"],
    ["pipeline", "Pipeline"],
    ["review", "Review"]
  ];
  return (
    <aside className="sidebar">
      <div className="brand">
        <strong>Kinetix</strong>
        <span>Director Console</span>
      </div>
      <nav className="nav-list" aria-label="Director console">
        {nav.map(([icon, label]) => (
          <button
            className={active === label ? "active" : ""}
            key={label}
            onClick={() => onActive(label)}
            type="button"
          >
            <Icon name={icon} />
            {label}
          </button>
        ))}
      </nav>
      <div className="project-tile">
        <span>Autosaved Draft</span>
        <strong>Neon Courier</strong>
        <small>Local story blueprint</small>
      </div>
    </aside>
  );
}

function Section({ title, action, children }: { title: string; action?: ReactNode; children: ReactNode }) {
  return (
    <section className="section">
      <div className="section-heading">
        <h2>{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

function OptionGrid({
  options,
  selected,
  onSelect,
  variant = "standard"
}: {
  options: Option[];
  selected: string;
  onSelect: (id: string) => void;
  variant?: "standard" | "compact" | "visual";
}) {
  return (
    <div className={`option-grid ${variant}`}>
      {options.map((option) => (
        <button
          className={`option-card ${selected === option.id ? "selected" : ""} ${option.tone || ""}`}
          data-option-id={option.id}
          key={option.id}
          onClick={() => onSelect(option.id)}
          type="button"
        >
          {variant === "visual" ? <span className={`visual-thumb visual-${option.id}`} /> : null}
          <span className="option-top">
            <strong>{option.label}</strong>
            {option.meta ? <em>{option.meta}</em> : null}
          </span>
          <small>{option.detail}</small>
        </button>
      ))}
    </div>
  );
}

function SegmentGroup({
  options,
  selected,
  onSelect,
  label
}: {
  options: Option[];
  selected: string;
  onSelect: (id: string) => void;
  label: string;
}) {
  return (
    <div className="segment-block">
      <span>{label}</span>
      <div className="segments">
        {options.map((option) => (
          <button
            className={selected === option.id ? "active" : ""}
            data-option-id={option.id}
            key={option.id}
            onClick={() => onSelect(option.id)}
            type="button"
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function ToggleRow({
  label,
  detail,
  checked,
  onChange
}: {
  label: string;
  detail: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <button className={`toggle-row ${checked ? "on" : ""}`} onClick={() => onChange(!checked)} type="button">
      <span>
        <strong>{label}</strong>
        <small>{detail}</small>
      </span>
      <b aria-hidden="true" />
    </button>
  );
}

function PreviousProjectSetup({
  state,
  patch
}: {
  state: StoryState;
  patch: (changes: Partial<StoryState>) => void;
}) {
  function selectSourceProject(sourceProject: string) {
    const preset = sourceProjectPresets[sourceProject] || {};
    const next: Partial<StoryState> = { sourceProject };

    if (state.inheritVisualDNA) {
      if (preset.genre) next.genre = preset.genre;
      if (preset.tone) next.tone = preset.tone;
      if (preset.era) next.era = preset.era;
      if (preset.visual) next.visual = preset.visual;
    }
    if (state.inheritDirector) {
      if (preset.director) next.director = preset.director;
    }
    if (state.inheritAspect) {
      if (preset.aspect) next.aspect = preset.aspect;
    }
    if (state.inheritEvolink) {
      if (preset.evolinkModel) next.evolinkModel = preset.evolinkModel;
      if (preset.videoResolution) next.videoResolution = preset.videoResolution;
    }
    if (state.inheritCharacters) {
      if (preset.characterDepth) next.characterDepth = preset.characterDepth;
      if (preset.castSize) next.castSize = preset.castSize;
      if (preset.leadType) next.leadType = preset.leadType;
      if (preset.supportType) next.supportType = preset.supportType;
      if (preset.antagonistType) next.antagonistType = preset.antagonistType;
      if (preset.characterLook) next.characterLook = preset.characterLook;
    }

    const modelId = next.evolinkModel || state.evolinkModel;
    const resolutionId = next.videoResolution || state.videoResolution;
    next.videoResolution = normalizeVideoResolution(modelId, resolutionId);
    patch(next);
  }

  const selectedMode = findOption(projectModes, state.projectMode);
  const selectedSource = findOption(sourceProjects, state.sourceProject);
  const inheritedCount = [
    state.inheritCharacters,
    state.inheritVisualDNA,
    state.inheritDirector,
    state.inheritAspect,
    state.inheritEvolink
  ].filter(Boolean).length;

  return (
    <Section
      title="Previous Project Setup"
      action={<span className="soft-count">{selectedMode.label}{state.projectMode !== "new_story" ? ` · ${selectedSource.label}` : ""}</span>}
    >
      <OptionGrid
        options={projectModes}
        selected={state.projectMode}
        onSelect={(projectMode) => patch({ projectMode })}
        variant="compact"
      />
      {state.projectMode !== "new_story" ? (
        <div className="memory-panel">
          <div>
            <h3>Source Project</h3>
            <OptionGrid
              options={sourceProjects}
              selected={state.sourceProject}
              onSelect={selectSourceProject}
              variant="compact"
            />
          </div>
          <div className="inheritance-grid">
            <ToggleRow
              checked={state.inheritCharacters}
              detail="Lead, support, antagonist, wardrobe, and face continuity."
              label="Character Bible"
              onChange={(inheritCharacters) => patch({ inheritCharacters })}
            />
            <ToggleRow
              checked={state.inheritVisualDNA}
              detail="Genre, tone, era, world rules, and visual preset."
              label="Visual DNA"
              onChange={(inheritVisualDNA) => patch({ inheritVisualDNA })}
            />
            <ToggleRow
              checked={state.inheritDirector}
              detail="Director language, camera behavior, and style profile."
              label="Director Style"
              onChange={(inheritDirector) => patch({ inheritDirector })}
            />
            <ToggleRow
              checked={state.inheritAspect}
              detail="Aspect ratio and social or cinematic framing target."
              label="Format Settings"
              onChange={(inheritAspect) => patch({ inheritAspect })}
            />
            <ToggleRow
              checked={state.inheritEvolink}
              detail="Seedance model, generation resolution, and upscale plan."
              label="EvoLink Settings"
              onChange={(inheritEvolink) => patch({ inheritEvolink })}
            />
          </div>
          <div className="range-control plot-flex">
            <div>
              <strong>Plot Freedom</strong>
              <small>{state.plotFlex}/5 · {state.plotFlex >= 4 ? "new plot, same world and production language" : "close continuation with tighter canon"}</small>
            </div>
            <input
              min="1"
              max="5"
              type="range"
              value={state.plotFlex}
              onChange={(event) => patch({ plotFlex: Number(event.target.value) })}
            />
          </div>
          <p className="memory-note">
            {inheritedCount} setup group{inheritedCount === 1 ? "" : "s"} will carry forward; change the story seed below to make a new episode, trailer, side plot, or alternate cut.
          </p>
        </div>
      ) : null}
    </Section>
  );
}

function BlueprintPanel({
  blueprint,
  referenceCounts,
  renderPlan,
  sendReady
}: {
  blueprint: Blueprint;
  referenceCounts: ReferenceCounts;
  renderPlan: RenderPlan;
  sendReady: boolean;
}) {
  return (
    <aside className="blueprint">
      <div className="blueprint-title">
        <div>
          <span>Story Blueprint</span>
          <strong>{blueprint.title}</strong>
        </div>
        <button type="button" aria-label="Export blueprint">
          <Icon name="export" />
        </button>
      </div>

      <div className="blueprint-card logline">
        <h3>Logline</h3>
        <p>{blueprint.logline}</p>
      </div>

      <div className="blueprint-card">
        <h3>Story Object</h3>
        <p>{blueprint.storyObject}</p>
      </div>

      <div className="blueprint-card">
        <div className="metric-row">
          <span>
            <strong>{blueprint.runtimeLabel}</strong>
            Runtime
          </span>
          <span>
            <strong>{blueprint.sceneCount}</strong>
            Scenes
          </span>
          <span>
            <strong>{blueprint.chunkCount}</strong>
            Clips
          </span>
        </div>
      </div>

      <div className="blueprint-card">
        <h3>Arc Preview</h3>
        <div className="act-list">
          {blueprint.acts.map((act) => (
            <article key={act.label}>
              <b>{act.time}</b>
              <strong>{act.label}</strong>
              <p>{act.text}</p>
            </article>
          ))}
        </div>
      </div>

      <div className="blueprint-card">
        <h3>Logic Pass</h3>
        <div className="blueprint-logic-list">
          {blueprint.logicBeats.map((beat) => (
            <article key={beat.label}>
              <b>{beat.time}</b>
              <strong>{beat.label}</strong>
              <span>{beat.visibleAction}</span>
            </article>
          ))}
        </div>
      </div>

      <div className="blueprint-card">
        <h3>Key Characters</h3>
        <div className="character-strip">
          {blueprint.characters.map((character, index) => (
            <span key={character}>
              <b>{index + 1}</b>
              {character}
            </span>
          ))}
        </div>
      </div>

      <div className="blueprint-card">
        <h3>Pipeline Stages</h3>
        <div className="stage-list">
          {blueprint.stages.map((stage) => (
            <article className={stage.ready ? "ready" : ""} key={stage.label}>
              <span aria-hidden="true" />
              <div>
                <strong>{stage.label}</strong>
                <small>{stage.detail}</small>
              </div>
            </article>
          ))}
        </div>
      </div>

      <div className={`blueprint-card send-readiness ${sendReady ? "ready" : ""}`}>
        <h3>EvoLink Readiness</h3>
        <div className="blueprint-mini-list">
          <span>
            <b>{renderPlan.providerQuality}</b>
            Generation
          </span>
          <span>
            <b>{renderPlan.aspectRatio}</b>
            Aspect
          </span>
          <span>
            <b>{renderPlan.chunkCount}</b>
            Chunks
          </span>
          <span>
            <b>{referenceCounts.ready}/{referenceCounts.total}</b>
            Refs
          </span>
        </div>
        <p>{sendReady ? "Approved package is ready for the server-side render gate." : "Review package, estimate, and approval gates before provider send."}</p>
      </div>

      <div className="blueprint-card mood-reference">
        <img src="/assets/director-console-concept.png" alt="" />
        <div>
          <h3>Output Shape</h3>
          <p>{blueprint.outputShape}</p>
        </div>
      </div>
    </aside>
  );
}

function StoryLogicPanel({ blueprint }: { blueprint: Blueprint }) {
  return (
    <Section title="Script Logic Pass" action={<span className="review-status ready">Hardened</span>}>
      <div className="script-doctor-panel">
        <div className="logic-rule-strip" aria-label="Script hardening rules">
          <span>
            <b>Cause</b>
            therefore/but beats
          </span>
          <span>
            <b>Action</b>
            visible escape mechanics
          </span>
          <span>
            <b>Bridge</b>
            no unexplained jumps
          </span>
          <span>
            <b>Payoff</b>
            setup returns on screen
          </span>
          <span>
            <b>Physics</b>
            solid doors and vehicles
          </span>
          <span>
            <b>Blocking</b>
            marks, axis, end frame
          </span>
          <span>
            <b>MacGuffin</b>
            carried forward
          </span>
          <span>
            <b>Refs</b>
            @Image roles locked
          </span>
          <span>
            <b>Audio</b>
            one gag or line
          </span>
        </div>

        <div className="logic-beat-grid">
          {blueprint.logicBeats.map((beat) => (
            <article className="logic-beat-card" key={beat.label}>
              <header>
                <span>{beat.time}</span>
                <strong>{beat.label}</strong>
              </header>
              <p>{beat.purpose}</p>
              <dl>
                <div>
                  <dt>Threat</dt>
                  <dd>{beat.threat}</dd>
                </div>
                <div>
                  <dt>Choice</dt>
                  <dd>{beat.choice}</dd>
                </div>
                <div>
                  <dt>Visible Action</dt>
                  <dd>{beat.visibleAction}</dd>
                </div>
                <div>
                  <dt>Consequence</dt>
                  <dd>{beat.consequence}</dd>
                </div>
                <div>
                  <dt>Bridge</dt>
                  <dd>{beat.locationBridge}</dd>
                </div>
              </dl>
              <div className="logic-blocking-map">
                <span>Blocking</span>
                <strong>{beat.blockingPlan.objectMark}</strong>
                <p>{beat.blockingPlan.movementPath}</p>
                <small>{beat.blockingPlan.cameraAxis}</small>
              </div>
              <small>{beat.promptRule}</small>
            </article>
          ))}
        </div>

        <div className="skill-source-grid">
          {blueprint.coherenceRules.map((rule) => (
            <article className="skill-source-card" key={rule.label}>
              <span>{rule.label}</span>
              <strong>{rule.contract}</strong>
              <p>{rule.promptRule}</p>
            </article>
          ))}
        </div>

        <div className="skill-source-grid">
          {blueprint.skillSources.map((source) => (
            <article className="skill-source-card" key={`${source.label}-${source.source}`}>
              <span>{source.scope}</span>
              <strong>{source.label}</strong>
              <p>{source.rule}</p>
              <small>{source.source}</small>
            </article>
          ))}
        </div>
      </div>
    </Section>
  );
}

function EvolinkSendPanel({
  blueprint,
  state,
  renderPlan,
  referenceCounts,
  referencesRequired,
  referencesReady,
  gates,
  approvedCapCredits,
  handoff,
  generatedAt,
  onGateChange,
  onCapChange,
  onBuildPackage,
  onReviewEstimate,
  onSend
}: {
  blueprint: Blueprint;
  state: StoryState;
  renderPlan: RenderPlan;
  referenceCounts: ReferenceCounts;
  referencesRequired: boolean;
  referencesReady: boolean;
  gates: RenderGates;
  approvedCapCredits: number;
  handoff: RenderHandoffState;
  generatedAt: string | null;
  onGateChange: (key: RenderGateKey, checked: boolean) => void;
  onCapChange: (value: number) => void;
  onBuildPackage: () => void;
  onReviewEstimate: () => void;
  onSend: () => void;
}) {
  const packageReady = Boolean(handoff.packageBuiltAt);
  const estimateReady = Boolean(handoff.estimateReviewedAt);
  const gatesReady = allRenderGatesApproved(gates);
  const capReady = approvedCapCredits > 0;
  const canEstimate = Boolean(generatedAt && packageReady);
  const canSend = Boolean(generatedAt && packageReady && estimateReady && referencesReady && gatesReady && capReady && handoff.status !== "sending" && handoff.status !== "sent");
  const displayEstimate = handoff.estimateCredits ?? renderPlan.estimatedCredits;
  const statusLabel = handoff.status === "sent"
    ? "Sent"
    : canSend
      ? "Ready"
      : referencesRequired && !referencesReady
        ? "Refs Needed"
      : generatedAt
        ? "Review"
        : "Blueprint Needed";

  return (
    <Section title="Review & Send to EvoLink" action={<span className={`review-status ${canSend ? "ready" : ""}`}>{statusLabel}</span>}>
      <div className="send-console">
        <div className="send-timeline" aria-label="EvoLink send stages">
          <article className={generatedAt ? "done" : ""}>
            <span>01</span>
            <div>
              <strong>Story Blueprint</strong>
              <small>{generatedAt ? `Created at ${generatedAt}` : "Waiting for generation"}</small>
            </div>
          </article>
          <article className={packageReady ? "done" : ""}>
            <span>02</span>
            <div>
              <strong>Render Package</strong>
              <small>{packageReady ? `Dry-run package built at ${handoff.packageBuiltAt}` : `${renderPlan.sceneCount} scenes and ${renderPlan.chunkCount} chunks staged`}</small>
            </div>
          </article>
          <article className={referencesReady ? "done" : ""}>
            <span>03</span>
            <div>
              <strong>References</strong>
              <small>{referencesRequired ? `${referenceCounts.ready} of ${referenceCounts.total} image references ready` : "Reference generation off"}</small>
            </div>
          </article>
          <article className={estimateReady ? "done" : ""}>
            <span>04</span>
            <div>
              <strong>Cost Gate</strong>
              <small>{estimateReady ? `${displayEstimate.toLocaleString()} estimated credits reviewed` : `${renderPlan.estimatedCredits.toLocaleString()} local estimate`}</small>
            </div>
          </article>
          <article className={gatesReady ? "done" : ""}>
            <span>05</span>
            <div>
              <strong>Approvals</strong>
              <small>{renderGateOptions.filter((gate) => gates[gate.id]).length} of {renderGateOptions.length} gates approved</small>
            </div>
          </article>
          <article className={handoff.status === "sent" ? "done" : ""}>
            <span>06</span>
            <div>
              <strong>Provider Send</strong>
              <small>{handoff.jobId ? handoff.jobId : "Server-side EvoLink handoff"}</small>
            </div>
          </article>
        </div>

        <div className="payload-panel">
          <h3>Provider Payload</h3>
          <div className="payload-list">
            {renderPlan.payloadLines.map((line) => (
              <span key={line.label}>
                <b>{line.label}</b>
                {line.value}
              </span>
            ))}
            <span>
              <b>Cap</b>
              {approvedCapCredits.toLocaleString()} credits
            </span>
            <span>
              <b>Image refs</b>
              {referencesRequired ? `${referenceCounts.ready}/${referenceCounts.total} ready` : "Off"}
            </span>
          </div>
          <div className="payload-summary">
            <strong>{blueprint.title}</strong>
            <small>{findOption(formats, state.format).label} package using {renderPlan.modelLabel}, {renderPlan.deliveryResolution}.</small>
          </div>
        </div>
      </div>

      <div className="approval-console">
        <div className="cap-field">
          <label htmlFor="approved-cap">Approved Credit Cap</label>
          <input
            id="approved-cap"
            min="1"
            step="25"
            type="number"
            value={approvedCapCredits}
            onChange={(event) => onCapChange(Math.max(0, Number(event.target.value) || 0))}
          />
          <small>Estimate: {displayEstimate.toLocaleString()} credits. The backend stops before crossing the approved cap.</small>
        </div>

        <div className="gate-grid">
          {renderGateOptions.map((gate) => (
            <ToggleRow
              checked={gates[gate.id]}
              detail={gate.detail}
              key={gate.id}
              label={gate.label}
              onChange={(checked) => onGateChange(gate.id, checked)}
            />
          ))}
        </div>
      </div>

      <div className={`send-status ${handoff.status}`}>
        <span>{handoff.message || (referencesRequired && !referencesReady
          ? "Generate or connect reference assets before the live EvoLink send."
          : "Build the package, review the estimate, approve the gates, then send through the backend render gate.")}</span>
        <div className="send-actions">
          <button disabled={!generatedAt} onClick={onBuildPackage} type="button">Build Package</button>
          <button disabled={!canEstimate || handoff.status === "estimating"} onClick={onReviewEstimate} type="button">
            {handoff.status === "estimating" ? "Checking Estimate" : "Review Estimate"}
          </button>
          <button className="send-primary" disabled={!canSend} onClick={onSend} type="button">
            {handoff.status === "sending" ? "Sending" : "Send to EvoLink"}
          </button>
        </div>
      </div>
    </Section>
  );
}

function ScreenplayPanel({
  state,
  screenplay,
  status,
  message,
  engine,
  note,
  stale,
  onNoteChange,
  onGenerate,
  onRefine,
  onClear,
  onSpokenDialogueChange,
  onProviderChange,
  onVary,
  onImport,
  onPunchUp,
  onCoverage,
  coverage
}: {
  state: StoryState;
  screenplay: Screenplay | null;
  status: ScriptStatus;
  message: string;
  engine: string;
  note: string;
  stale: boolean;
  onNoteChange: (value: string) => void;
  onGenerate: () => void;
  onRefine: () => void;
  onClear: () => void;
  onSpokenDialogueChange: (checked: boolean) => void;
  onProviderChange: (provider: string) => void;
  onVary: (warning: ShotLintWarning) => void;
  onImport: (text: string) => Promise<boolean>;
  onPunchUp: () => void;
  onCoverage: () => void;
  coverage: { status: string; notes: CoverageNotes | null; message: string };
}) {
  const busy = status === "generating" || status === "refining";
  const lintWarnings = useMemo(() => (screenplay ? lintScreenplayShots(screenplay) : []), [screenplay]);
  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState("");
  const [importing, setImporting] = useState(false);

  async function submitImport() {
    if (!importText.trim() || importing) return;
    setImporting(true);
    const accepted = await onImport(importText);
    setImporting(false);
    if (accepted) {
      setImportText("");
      setImportOpen(false);
    }
  }
  const statusLabel = busy
    ? (status === "refining" ? "Rewriting" : "Writing")
    : screenplay
      ? (stale ? "Draft Stale" : "Draft Ready")
      : "Template Mode";
  const spoken = spokenDialogueActive(state);
  return (
    <Section
      title="Screenplay — Kinetix Script Skill"
      action={<span className={`review-status ${screenplay && !stale ? "ready" : ""}`}>{statusLabel}</span>}
    >
      <div className="screenplay-panel">
        <div className="screenplay-actions">
          <button className="mini-action screenplay-primary" disabled={busy} onClick={onGenerate} type="button">
            <Icon name="spark" />
            {busy && status === "generating" ? "Writing Screenplay" : screenplay ? "Write New Draft" : "Write Screenplay"}
          </button>
          {screenplay ? (
            <button className="mini-action" disabled={busy} onClick={onPunchUp} type="button" title="A dedicated table pass: sharpen jokes, buttons, and dialogue without restructuring.">
              Punch Up
            </button>
          ) : null}
          {screenplay ? (
            <button className="mini-action" disabled={busy || coverage.status === "running"} onClick={onCoverage} type="button" title="Studio coverage: an analyst scores premise, structure, dialogue, characters, and originality with a PASS / CONSIDER / RECOMMEND verdict.">
              {coverage.status === "running" ? "Reading…" : "Coverage"}
            </button>
          ) : null}
          {screenplay ? (
            <button className="mini-action" disabled={busy} onClick={onClear} type="button">Clear Draft</button>
          ) : null}
          <button className="mini-action" disabled={busy} onClick={() => setImportOpen((open) => !open)} type="button">
            {importOpen ? "Close Import" : "Import JSON"}
          </button>
          <ToggleRow
            checked={state.spokenDialogue}
            detail={spoken
              ? "Characters speak their exact scripted lines on camera in dialogue shots."
              : state.spokenDialogue
                ? "Dialogue lane or generated audio is off, so clips play visual-first."
                : "Visual-first storytelling; at most one landing line per scene."}
            label="Spoken Dialogue"
            onChange={onSpokenDialogueChange}
          />
        </div>

        <div className="screenplay-engine-row">
          <SegmentGroup
            label="Script Engine"
            options={scriptProviders}
            selected={state.scriptProvider}
            onSelect={onProviderChange}
          />
          <small className="writers-room-note">Writers' room always on: architecture pass, then scenes, then shots — with automatic single-pass fallback if a stage fails.</small>
          <small>{findOption(scriptProviders, state.scriptProvider).detail}{engine ? ` Last draft: ${engine}.` : ""}</small>
        </div>

        {importOpen ? (
          <div className="screenplay-import">
            <textarea
              aria-label="Screenplay JSON"
              disabled={importing}
              placeholder="Paste screenplay JSON written with the kinetix-screenwriter skill (Claude/Codex writes it free with your subscription — no API key needed). It will be validated and linted before it drives cards, prompts, and renders."
              value={importText}
              onChange={(event) => setImportText(event.target.value)}
            />
            <button className="mini-action screenplay-primary" disabled={importing || !importText.trim()} onClick={() => void submitImport()} type="button">
              {importing ? "Validating…" : "Validate & Use"}
            </button>
          </div>
        ) : null}

        {message ? <p className={`screenplay-message ${status}`}>{message}</p> : null}
        {stale && screenplay ? (
          <p className="screenplay-message error">Story settings changed since this draft was written. Write a new draft, or keep it deliberately.</p>
        ) : null}

        {screenplay ? (
          <div className="screenplay-body">
            <div className="screenplay-head">
              <div>
                <strong>{screenplay.title}</strong>
                <p>{screenplay.logline}</p>
              </div>
              <div className="screenplay-meta">
                {engine ? <span>{engine}</span> : null}
                <span>{screenplay.scenes.length} scenes · {screenplay.shots.length} shots · {minutes(screenplay.runtimeSeconds)}</span>
                {screenplay.theme ? <span>Theme: {screenplay.theme}</span> : null}
                {screenplay.comicEngine ? <span>Comic engine: {screenplay.comicEngine}</span> : null}
              </div>
            </div>

            {screenplay.beats && screenplay.beats.length ? (
              <div className="screenplay-beats">
                {screenplay.beats.map((beat, index) => (
                  <article key={beat.id}>
                    <b>{index + 1}</b>
                    <strong>{beat.label}</strong>
                    <p>{beat.purpose}</p>
                    {beat.turn ? <small>Turn: {beat.turn}</small> : null}
                  </article>
                ))}
              </div>
            ) : null}

            <div className="screenplay-cast">
              {screenplay.characters.map((character) => (
                <article key={character.id}>
                  <strong>{character.name}</strong>
                  <span>{screenplayRoleLabel(character.role)}</span>
                  <p>{character.look}</p>
                  <small>
                    {character.want ? `Wants ${character.want} ` : ""}
                    {character.flaw ? `· Flaw: ${character.flaw}` : ""}
                  </small>
                </article>
              ))}
            </div>

            <div className="screenplay-scenes">
              {screenplay.scenes.map((scene) => {
                const sceneShots = screenplay.shots.filter((shot) => shot.sceneId === scene.id);
                return (
                  <article key={scene.id}>
                    <header>
                      <strong>{scene.slugline}</strong>
                      <span>{sceneShots.length} shot{sceneShots.length === 1 ? "" : "s"}</span>
                    </header>
                    <p>{scene.summary}</p>
                    {scene.turn ? <small className="scene-turn">Turn: {scene.turn}</small> : null}
                    {scene.dialogue.length ? (
                      <div className="scene-dialogue">
                        {scene.dialogue.map((line, index) => (
                          <span key={`${scene.id}-line-${index}`}>
                            <b>{line.character}</b>
                            “{line.line}”
                            {line.delivery ? <i> — {line.delivery}</i> : null}
                          </span>
                        ))}
                      </div>
                    ) : null}
                    <div className="scene-shots">
                      {sceneShots.map((shot) => (
                        <span key={shot.id}>
                          <b>{shot.durationSeconds}s</b>
                          {shot.shotType ? `${shot.shotType}: ` : ""}{shot.purpose || shot.action.slice(0, 90)}
                        </span>
                      ))}
                    </div>
                  </article>
                );
              })}
            </div>

            {screenplay.finalImage ? (
              <p className="screenplay-final"><b>Final image:</b> {screenplay.finalImage}</p>
            ) : null}

            {coverage.status === "error" ? <p className="screenplay-message error">{coverage.message}</p> : null}
            {coverage.notes ? (
              <div className={`coverage-card ${String(coverage.notes.verdict || "").toLowerCase()}`}>
                <header>
                  <strong>{coverage.notes.verdict || "COVERAGE"}</strong>
                  <span>{Object.entries(coverage.notes.scores || {}).map(([key, value]) => `${key} ${value}`).join(" · ")}</span>
                </header>
                {coverage.notes.verdictReason ? <p>{coverage.notes.verdictReason}</p> : null}
                {(coverage.notes.concerns || []).slice(0, 3).map((concern, index) => (
                  <small key={`concern-${index}`}>⚠ {concern}</small>
                ))}
                {(coverage.notes.strengths || []).slice(0, 2).map((strength, index) => (
                  <small key={`strength-${index}`}>✓ {strength}</small>
                ))}
              </div>
            ) : null}

            <div className="shot-lint">
              <div className="shot-lint-head">
                <strong>Shot Linter</strong>
                <span className={lintWarnings.length ? "lint-count warn" : "lint-count"}>
                  {lintWarnings.length ? `${lintWarnings.length} warning${lintWarnings.length === 1 ? "" : "s"}` : "Clean — no duplicate-shot tells"}
                </span>
              </div>
              {lintWarnings.map((warning) => (
                <article key={`${warning.shotId}-${warning.kind}`}>
                  <p>{warning.message}</p>
                  <button className="mini-action" disabled={busy} onClick={() => onVary(warning)} type="button">
                    Vary Shot
                  </button>
                </article>
              ))}
            </div>

            <div className="screenplay-refine">
              <textarea
                aria-label="Director note"
                disabled={busy}
                placeholder='Director note — e.g. "Make the ending land harder", "More physical comedy in scene 2", "Cut the sidekick".'
                value={note}
                onChange={(event) => onNoteChange(event.target.value)}
              />
              <button className="mini-action" disabled={busy || !note.trim()} onClick={onRefine} type="button">
                {status === "refining" ? "Rewriting" : "Apply Director Note"}
              </button>
            </div>
          </div>
        ) : (
          <p className="screenplay-empty">
            No screenplay yet — renders currently use generic template beats. Two ways to get one: Write Screenplay uses the
            backend script engine (API key, per-call cost), or have Claude/Codex write it free with the kinetix-screenwriter
            skill (skills/kinetix-screenwriter/SKILL.md) and paste it via Import JSON. Either way you get named characters
            with wants and flaws, real dialogue, scene turns, and a continuity-chained shot list that becomes the exact
            Seedance prompts.
          </p>
        )}
      </div>
    </Section>
  );
}

function taskStatusLabel(status: string): string {
  if (status === "waiting_chain") return "Waiting for previous clip";
  if (status === "completed") return "Done";
  if (status === "failed") return "Failed";
  if (status === "pending") return "Queued";
  return status.charAt(0).toUpperCase() + status.slice(1);
}

function RenderJobPanel({
  job,
  jobError,
  qcReports,
  stitch,
  onRefresh,
  onQc,
  onStitch,
  onRetry,
  retryBusy
}: {
  job: RenderJob | null;
  jobError: string;
  qcReports: Record<string, ClipQcState>;
  stitch: StitchState;
  onRefresh: () => void;
  onQc: (task: RenderJobTask) => void;
  onStitch: () => void;
  onRetry: (task: RenderJobTask) => void;
  retryBusy: string;
}) {
  if (!job && !jobError) return null;
  const running = Boolean(job && ["running", "rendering", "pending"].includes(job.status));
  const doneCount = job ? job.tasks.filter((task) => task.status === "completed").length : 0;
  const stitchReady = Boolean(job && !running && doneCount > 0);
  const stitchBusy = stitch.status === "starting" || stitch.status === "running";
  return (
    <Section
      title="Render Job & Clip QC"
      action={<span className={`review-status ${job && job.status === "completed" ? "ready" : ""}`}>{job ? taskStatusLabel(job.status) : "Error"}</span>}
    >
      <div className="job-panel">
        <div className="job-topline">
          <div>
            <strong>{job ? job.title : "Render job"}</strong>
            <small>
              {job
                ? `${doneCount}/${job.tasks.length} clips done · ${Number(job.reservedCredits || 0).toFixed(1)}/${Number(job.approvedCapCredits || 0).toFixed(0)} credits reserved${job.continuityChaining ? " · continuity chain on" : ""}`
                : jobError}
            </small>
            {job?.stopReason ? <small className="job-stop">{job.stopReason}</small> : null}
          </div>
          <div className="job-topline-actions">
            <button className="mini-action" onClick={onRefresh} type="button">
              {running ? "Refreshing…" : "Refresh"}
            </button>
            <button className="mini-action screenplay-primary" disabled={!stitchReady || stitchBusy} onClick={onStitch} type="button">
              {stitchBusy ? "Stitching…" : stitch.status === "done" ? "Re-stitch Master" : "Stitch Master"}
            </button>
          </div>
        </div>

        {stitch.status !== "idle" ? (
          <div className={`stitch-strip ${stitch.status}`}>
            <span>
              {stitch.status === "done"
                ? "Master ready"
                : stitch.status === "failed"
                  ? "Stitch failed"
                  : stitch.step || stitch.message || "Stitching…"}
            </span>
            {stitch.status === "failed" ? <small>{stitch.message}</small> : null}
            {stitch.status === "running" && stitch.step.startsWith("queued") ? (
              <small>Waiting for your home render agent — start it with: npm run render:agent</small>
            ) : null}
            {stitch.status === "done" && stitch.masterKey ? (
              <video controls preload="metadata" src={`${API_BASE}/api/assets/object/${stitch.masterKey}`} />
            ) : null}
          </div>
        ) : null}

        {job ? (
          <div className="job-tasks">
            {job.tasks.map((task) => {
              const qc = task.videoKey ? qcReports[task.videoKey] : undefined;
              return (
                <article className={`job-task ${task.status}`} key={task.chunkId || task.label}>
                  <header>
                    <strong>{task.label || task.chunkId}</strong>
                    <span className={`job-status ${task.status}`}>{taskStatusLabel(task.status)}{typeof task.progress === "number" && task.status === "running" ? ` ${Math.round(task.progress)}%` : ""}</span>
                  </header>
                  {task.chainNote ? <small className="chain-note">{task.chainNote}</small> : null}
                  {task.error ? <small className="job-error">{task.error}</small> : null}
                  {["failed", "completed"].includes(task.status) ? (
                    <button
                      className="mini-action job-retry"
                      disabled={retryBusy === task.chunkId}
                      onClick={() => onRetry(task)}
                      type="button"
                    >
                      {retryBusy === task.chunkId ? "Retrying…" : task.status === "failed" ? "Retry Clip" : "Re-roll Clip"}
                    </button>
                  ) : null}
                  {task.videoKey ? (
                    <div className="job-clip">
                      <video controls preload="metadata" src={`${API_BASE}/api/assets/object/${task.videoKey}`} />
                      <div className="job-clip-actions">
                        <button
                          className="mini-action"
                          disabled={qc?.status === "running"}
                          onClick={() => onQc(task)}
                          type="button"
                        >
                          {qc?.status === "running" ? "Screening…" : qc?.notes ? "Re-run Clip QC" : "Run Clip QC"}
                        </button>
                        {qc?.notes && typeof qc.notes.score === "number" ? (
                          <b className={`qc-score ${qc.notes.score >= 7 ? "pass" : "flag"}`}>{qc.notes.score}/10</b>
                        ) : null}
                      </div>
                      {qc && qc.status !== "running" && qc.message && !qc.notes ? <small className="job-error">{qc.message}</small> : null}
                      {qc?.notes ? (
                        <div className="qc-report">
                          {qc.notes.verdict ? <p><b>{qc.notes.verdict}</b>{qc.notes.overall ? ` — ${qc.notes.overall}` : ""}</p> : null}
                          {(qc.notes.aiTells || []).slice(0, 4).map((tell, index) => (
                            <small key={`tell-${index}`}>⚠ {tell.at ? `${tell.at} · ` : ""}{tell.tell}{tell.fix ? ` → ${tell.fix}` : ""}</small>
                          ))}
                          {(qc.notes.polish || []).slice(0, 2).map((line, index) => (
                            <small key={`polish-${index}`}>✦ {line}</small>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </article>
              );
            })}
          </div>
        ) : null}
        <p className="job-note">
          Clip QC samples frames from a finished clip and screens them for garbled text, duplicate energy, and AI tells before you stitch or respend. Chained clips start automatically as each previous clip finishes — keep this page open or poll the job from your agent.
        </p>
      </div>
    </Section>
  );
}

function CharacterCreationStudio({
  state,
  patch
}: {
  state: StoryState;
  patch: (changes: Partial<StoryState>) => void;
}) {
  const lead = findOption(leadTypes, state.leadType);
  const support = findOption(supportTypes, state.supportType);
  const antagonist = findOption(antagonistTypes, state.antagonistType);
  return (
    <Section title="Character Creation">
      <div className="character-studio">
        <div className="character-preview">
          <div className="portrait-frame lead">
            <span>Lead</span>
            <strong>{lead.label}</strong>
          </div>
          <div className="portrait-frame support">
            <span>Support</span>
            <strong>{support.label}</strong>
          </div>
          <div className="portrait-frame antagonist">
            <span>Antagonist</span>
            <strong>{antagonist.label}</strong>
          </div>
        </div>
        <div className="character-controls">
          <div>
            <h3>Lead Character</h3>
            <OptionGrid options={leadTypes} selected={state.leadType} onSelect={(leadType) => patch({ leadType })} variant="compact" />
          </div>
          <div>
            <h3>Supporting Character</h3>
            <OptionGrid options={supportTypes} selected={state.supportType} onSelect={(supportType) => patch({ supportType })} variant="compact" />
          </div>
          <div>
            <h3>Antagonist</h3>
            <OptionGrid options={antagonistTypes} selected={state.antagonistType} onSelect={(antagonistType) => patch({ antagonistType })} variant="compact" />
          </div>
        </div>
      </div>
      <div className="quick-row character-output">
        <SegmentGroup
          options={characterLooks}
          selected={state.characterLook}
          onSelect={(characterLook) => patch({ characterLook })}
          label="ChatGPT Image 2 Character Output"
        />
        <div className="toggle-grid tight">
          <ToggleRow
            checked={state.characterCards}
            detail="Creates lead, support, antagonist, and optional cast cards."
            label="Generate Character Cards"
            onChange={(characterCards) => patch({ characterCards })}
          />
          <ToggleRow
            checked={state.faceLock}
            detail="Keeps faces, wardrobe, props, and silhouettes consistent across scenes."
            label="Continuity Lock"
            onChange={(faceLock) => patch({ faceLock })}
          />
        </div>
      </div>
    </Section>
  );
}

function referenceAssetStatusLabel(status: ReferenceAssetStatus): string {
  if (status === "not_configured") return "No key";
  if (status === "generated") return "Ready";
  if (status === "uploaded") return "Ready";
  if (status === "generating") return "Generating";
  if (status === "failed") return "Failed";
  if (status === "missing") return "Missing";
  return "Planned";
}

function ReferenceAssetList({
  assets,
  title,
  onUpload
}: {
  assets: ReferenceAsset[];
  title: string;
  onUpload: (asset: ReferenceAsset, file: File) => void;
}) {
  return (
    <div className="reference-lane">
      <div className="reference-lane-title">
        <strong>{title}</strong>
        <span>{assets.length}</span>
      </div>
      <div className="reference-list">
        {assets.length ? assets.map((asset) => (
          <article className="reference-item" key={`${asset.kind}-${asset.id}`}>
            <div>
              <strong>{asset.label}</strong>
              <span>{asset.role || asset.sceneLabel || asset.fileName}</span>
            </div>
            <div>
              <b className={`reference-status ${asset.status}`}>{referenceAssetStatusLabel(asset.status)}</b>
              <small>{asset.storageKey || asset.plannedStorageKey}</small>
              <label className="reference-upload">
                <input
                  accept="image/*"
                  disabled={asset.status === "generating"}
                  onChange={(event) => {
                    const file = event.currentTarget.files?.[0];
                    event.currentTarget.value = "";
                    if (file) onUpload(asset, file);
                  }}
                  type="file"
                />
                Upload own image
              </label>
            </div>
          </article>
        )) : (
          <p className="reference-empty">No references enabled. Plan names first, then generate or upload your own images.</p>
        )}
      </div>
    </div>
  );
}

function ReferenceAssetPanel({
  state,
  registry,
  counts,
  referencesRequired,
  referencesReady,
  onGenerate,
  onPlan,
  onUpload
}: {
  state: StoryState;
  registry: ReferenceRegistry;
  counts: ReferenceCounts;
  referencesRequired: boolean;
  referencesReady: boolean;
  onGenerate: () => void;
  onPlan: () => void;
  onUpload: (asset: ReferenceAsset, file: File) => void;
}) {
  const isGenerating = registry.status === "generating";
  const actionLabel = referencesReady ? "Ready" : registry.status === "not_configured" ? "Needs Key" : referencesRequired ? "Plan" : "Off";

  return (
    <Section title="Reference Assets" action={<span className={`review-status ${referencesReady ? "ready" : ""}`}>{actionLabel}</span>}>
      <div className="reference-console">
        <div className="reference-topline">
          <div className="reference-meter">
            <strong>{counts.ready}/{counts.total}</strong>
            <span>Ready image references</span>
          </div>
          <div className="reference-actions">
            <button className="mini-action" disabled={!referencesRequired || isGenerating} onClick={onPlan} type="button">
              Plan Reference Names
            </button>
            <button className="mini-action reference-primary" disabled={!referencesRequired || isGenerating} onClick={onGenerate} type="button">
              {isGenerating ? "Generating References" : "Generate with ChatGPT Image 2"}
            </button>
          </div>
        </div>

        <div className="reference-lanes">
          <ReferenceAssetList assets={registry.characterCards} onUpload={onUpload} title="Character Cards" />
          <ReferenceAssetList assets={registry.sceneReferences} onUpload={onUpload} title="Scene Plates" />
        </div>

        <div className="reference-foot">
          <span>{state.faceLock ? "Face lock on" : "Face lock off"}</span>
          <span>{state.storyboards ? "Storyboard refs on" : "Storyboard refs off"}</span>
          <span>{registry.updatedAt ? `Updated ${registry.updatedAt}` : "Not planned"}</span>
        </div>
        <p className="reference-note">{registry.message} Uploads are optional and can replace or supplement ChatGPT Image 2 reference generation.</p>
      </div>
    </Section>
  );
}

export function App() {
  const [active, setActive] = useState("Build");
  const [generatedAt, setGeneratedAt] = useState<string | null>(null);
  const [approvedCapCredits, setApprovedCapCredits] = useState(DEFAULT_RENDER_CAP);
  const [renderGates, setRenderGates] = useState<RenderGates>(defaultRenderGates);
  const [handoff, setHandoff] = useState<RenderHandoffState>({
    packageBuiltAt: null,
    estimateReviewedAt: null,
    estimateCredits: null,
    status: "idle",
    message: "",
    jobId: ""
  });
  const [referenceRegistry, setReferenceRegistry] = useState<ReferenceRegistry>(() => {
    try {
      const stored = window.localStorage.getItem(REFERENCE_STORAGE_KEY);
      return stored ? { ...createEmptyReferenceRegistry(), ...JSON.parse(stored) } : createEmptyReferenceRegistry();
    } catch {
      return createEmptyReferenceRegistry();
    }
  });
  const [state, setState] = useState<StoryState>(() => {
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      return stored ? { ...defaultState, ...JSON.parse(stored) } : defaultState;
    } catch {
      return defaultState;
    }
  });
  const [screenplay, setScreenplay] = useState<Screenplay | null>(() => {
    try {
      const stored = window.localStorage.getItem(SCRIPT_STORAGE_KEY);
      const parsed = stored ? JSON.parse(stored) : null;
      return parsed && Array.isArray(parsed.shots) && parsed.shots.length ? parsed as Screenplay : null;
    } catch {
      return null;
    }
  });
  const [scriptStatus, setScriptStatus] = useState<ScriptStatus>(() => (screenplay ? "ready" : "idle"));
  const [scriptMessage, setScriptMessage] = useState("");
  const [scriptEngine, setScriptEngine] = useState("");
  const [scriptNote, setScriptNote] = useState("");
  const [coverage, setCoverage] = useState<{ status: "idle" | "running" | "ready" | "error"; notes: CoverageNotes | null; message: string }>({ status: "idle", notes: null, message: "" });
  const [renderJob, setRenderJob] = useState<RenderJob | null>(null);
  const [renderJobError, setRenderJobError] = useState("");
  const [qcReports, setQcReports] = useState<Record<string, ClipQcState>>({});
  const [stitch, setStitch] = useState<StitchState>(idleStitchState);
  const [retryBusy, setRetryBusy] = useState("");
  const blueprint = useMemo(() => buildBlueprint(state, screenplay), [state, screenplay]);
  const renderPlan = useMemo(() => buildRenderPlan(state, blueprint), [state, blueprint]);
  const referenceCounts = useMemo(() => countReferenceRegistry(referenceRegistry), [referenceRegistry]);
  const referencesRequired = referencesRequiredForState(state);
  const referencesReady = !referencesRequired || (referenceCounts.total > 0 && referenceCounts.ready === referenceCounts.total);
  const sendReady = Boolean(
    generatedAt &&
    handoff.packageBuiltAt &&
    handoff.estimateReviewedAt &&
    referencesReady &&
    allRenderGatesApproved(renderGates) &&
    approvedCapCredits > 0 &&
    handoff.status !== "sending" &&
    handoff.status !== "sent"
  );

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [state]);

  useEffect(() => {
    window.localStorage.setItem(REFERENCE_STORAGE_KEY, JSON.stringify(referenceRegistry));
  }, [referenceRegistry]);

  useEffect(() => {
    if (screenplay) {
      window.localStorage.setItem(SCRIPT_STORAGE_KEY, JSON.stringify(screenplay));
    } else {
      window.localStorage.removeItem(SCRIPT_STORAGE_KEY);
    }
  }, [screenplay]);

  async function requestScreenplay(mode: "generate" | "refine" | "punch_up", noteOverride?: string) {
    const needsDraft = mode === "refine" || mode === "punch_up";
    if (needsDraft && !screenplay) return;
    setScriptStatus(mode === "generate" ? "generating" : "refining");
    setScriptMessage(mode === "refine"
      ? "The script skill is rewriting the draft around your director note."
      : mode === "punch_up"
        ? "The punch-up room is sharpening every joke, button, and line."
        : "The writers' room is working in passes: story architecture, then scenes, then shots.");
    try {
      const payload: Record<string, unknown> = {
        mode,
        brief: buildScriptBrief(state),
        provider: state.scriptProvider === "auto" ? "" : state.scriptProvider
      };
      if (needsDraft) {
        payload.screenplay = screenplay;
        if (mode === "refine") payload.note = (noteOverride ?? scriptNote).trim();
      }
      const result = await apiPost<ScriptGenerateResponse & { degradedFrom?: string }>("/api/script/generate", payload);
      if (!result.ok || !result.screenplay) {
        throw new Error(result.message || "The script engine did not return a screenplay.");
      }
      const degradedNote = result.degradedFrom
        ? ` (Heads up: the ${result.degradedFrom} pass failed, so this is a single-pass fallback draft — regenerate for the full writers' room version.)`
        : "";
      setScreenplay(result.screenplay);
      setScriptStatus("ready");
      setScriptEngine([result.provider, result.model].filter(Boolean).join(" · "));
      setScriptMessage(mode === "refine"
        ? `Rewrite complete: "${result.screenplay.title}" updated around your note.`
        : mode === "punch_up"
          ? `Punch-up pass complete: "${result.screenplay.title}" sharpened.`
          : `Original draft ready: "${result.screenplay.title}" — ${result.screenplay.scenes.length} scenes, ${result.screenplay.shots.length} continuity-chained shots.${degradedNote}`);
      if (mode === "refine") setScriptNote("");
      setCoverage({ status: "idle", notes: null, message: "" });
      setReferenceRegistry(createEmptyReferenceRegistry("Screenplay updated. Plan and generate references for the new cast and scenes."));
      markRenderPackageStale("Screenplay updated. Build the render package again so prompts use the new script.");
    } catch (error) {
      setScriptStatus(screenplay ? "ready" : "error");
      setScriptMessage(`Script skill unavailable: ${error instanceof Error ? error.message : "unknown error"}${screenplay ? " — keeping the previous draft." : ". Template beats stay active as the fallback."}`);
    }
  }

  async function refreshRenderJob(jobId?: string) {
    const id = jobId || handoff.jobId;
    if (!id) return;
    try {
      const result = await apiGet<{ ok: boolean; job?: RenderJob; message?: string }>(`/api/render/jobs/${id}`);
      if (result.ok && result.job) {
        setRenderJob(result.job);
        setRenderJobError("");
      } else {
        setRenderJobError(result.message || "Job status unavailable.");
      }
    } catch (error) {
      setRenderJobError(`Job status unavailable: ${error instanceof Error ? error.message : "unknown error"}.`);
    }
  }

  useEffect(() => {
    if (!handoff.jobId) return;
    void refreshRenderJob(handoff.jobId);
    const timer = window.setInterval(() => {
      setRenderJob((current) => {
        const active = !current || ["running", "rendering", "pending"].includes(current.status);
        if (active) void refreshRenderJob(handoff.jobId);
        return current;
      });
    }, 8000);
    return () => window.clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handoff.jobId]);

  async function startStitch() {
    if (!renderJob || stitch.status === "starting" || stitch.status === "running") return;
    setStitch({ ...idleStitchState, status: "starting", message: "Queueing the master stitch…" });
    try {
      const result = await apiPost<{ ok: boolean; status: string; stitchId?: string; message?: string }>("/api/render/stitch/start", {
        jobId: renderJob.id,
        title: blueprint.title,
        look: buildStitchLook(state)
      });
      if (!result.ok || !result.stitchId) throw new Error(result.message || `Stitch gate returned ${result.status}.`);
      setStitch({ ...idleStitchState, status: "running", stitchId: result.stitchId, message: "Stitching…" });
    } catch (error) {
      setStitch({ ...idleStitchState, status: "failed", message: `Stitch failed to start: ${error instanceof Error ? error.message : "unknown error"}.` });
    }
  }

  useEffect(() => {
    if (stitch.status !== "running" || !stitch.stitchId) return;
    const timer = window.setInterval(async () => {
      try {
        const result = await apiGet<{ ok: boolean; stitch?: { status?: string; message?: string; progress?: { step?: string }; result?: { storageKey?: string } } }>(`/api/render/stitch/jobs/${stitch.stitchId}`);
        const record = result.stitch || {};
        const status = String(record.status || "running");
        if (status === "done") {
          setStitch((current) => ({ ...current, status: "done", step: "", message: "Master ready.", masterKey: String(record.result?.storageKey || "") }));
        } else if (status === "failed") {
          setStitch((current) => ({ ...current, status: "failed", message: record.message || "Stitch failed." }));
        } else {
          setStitch((current) => ({ ...current, step: String(record.progress?.step || ""), message: record.message || current.message }));
        }
      } catch {
        // transient poll failure — keep trying
      }
    }, 5000);
    return () => window.clearInterval(timer);
  }, [stitch.status, stitch.stitchId]);

  async function retryClip(task: RenderJobTask) {
    if (!renderJob || retryBusy) return;
    setRetryBusy(task.chunkId);
    try {
      const result = await apiPost<{ ok: boolean; status: string; message?: string; job?: RenderJob }>("/api/render/retry", {
        jobId: renderJob.id,
        chunkId: task.chunkId
      });
      if (result.ok && result.job) {
        if (task.videoKey) {
          setQcReports((current) => {
            const next = { ...current };
            delete next[task.videoKey as string];
            return next;
          });
        }
        setRenderJob(result.job);
        setRenderJobError("");
      } else {
        setRenderJobError(result.message || `Retry returned ${result.status}.`);
      }
    } catch (error) {
      setRenderJobError(`Retry failed: ${error instanceof Error ? error.message : "unknown error"}.`);
    }
    setRetryBusy("");
  }

  async function runClipQc(task: RenderJobTask) {
    const storageKey = task.videoKey || "";
    if (!storageKey) return;
    setQcReports((current) => ({ ...current, [storageKey]: { status: "running", message: "Screening frames…", notes: null } }));
    try {
      const result = await apiPost<{ ok: boolean; status: string; message?: string; notes?: ClipQcNotes }>("/api/dailies", {
        storageKey,
        style: "clip",
        context: { title: blueprint.title, beats: [{ label: task.label }] }
      });
      if (result.ok && result.notes) {
        setQcReports((current) => ({ ...current, [storageKey]: { status: "ready", message: "", notes: result.notes || null } }));
      } else if (result.status === "agent_pending") {
        setQcReports((current) => ({ ...current, [storageKey]: { status: "pending_agent", message: result.message || "Queued for your home render agent — run npm run render:agent, then tap QC again.", notes: null } }));
      } else {
        setQcReports((current) => ({ ...current, [storageKey]: { status: "error", message: result.message || `QC returned ${result.status}.`, notes: null } }));
      }
    } catch (error) {
      setQcReports((current) => ({ ...current, [storageKey]: { status: "error", message: `QC failed: ${error instanceof Error ? error.message : "unknown error"}.`, notes: null } }));
    }
  }

  async function importScreenplay(text: string): Promise<boolean> {
    let parsed: Record<string, unknown>;
    try {
      parsed = JSON.parse(text) as Record<string, unknown>;
    } catch {
      setScriptStatus(screenplay ? "ready" : "error");
      setScriptMessage("That isn't valid JSON — paste the exact screenplay object the skill returned.");
      return false;
    }
    try {
      const result = await apiPost<{ ok: boolean; status: string; message?: string; screenplay?: Screenplay; warnings?: Array<{ message: string }> }>(
        "/api/script/validate",
        { screenplay: parsed }
      );
      if (!result.ok || !result.screenplay) {
        throw new Error(result.message || "Validation failed.");
      }
      setScreenplay(result.screenplay);
      setScriptStatus("ready");
      setScriptEngine("external · kinetix-screenwriter skill");
      const warningCount = result.warnings?.length || 0;
      setScriptMessage(`Imported "${result.screenplay.title}" — ${result.screenplay.scenes.length} scenes, ${result.screenplay.shots.length} shots.${warningCount ? ` ${warningCount} lint warning${warningCount === 1 ? "" : "s"} flagged below — fix with Vary Shot or a director note.` : " Lint clean."}`);
      setReferenceRegistry(createEmptyReferenceRegistry("Screenplay imported. Plan and generate references for the new cast and scenes."));
      markRenderPackageStale("Screenplay imported. Build the render package again so prompts use the new script.");
      return true;
    } catch (error) {
      setScriptStatus(screenplay ? "ready" : "error");
      setScriptMessage(`Import failed: ${error instanceof Error ? error.message : "unknown error"}.`);
      return false;
    }
  }

  async function getCoverage() {
    if (!screenplay || coverage.status === "running") return;
    setCoverage({ status: "running", notes: null, message: "The story analyst is reading the draft…" });
    try {
      const result = await apiPost<{ ok: boolean; answer?: CoverageNotes; message?: string }>("/api/assistant", {
        mode: "coverage",
        context: { scriptText: screenplayToText(screenplay), title: screenplay.title }
      });
      if (!result.ok || !result.answer) throw new Error(result.message || "Coverage unavailable.");
      setCoverage({ status: "ready", notes: result.answer, message: "" });
    } catch (error) {
      setCoverage({ status: "error", notes: null, message: `Coverage failed: ${error instanceof Error ? error.message : "unknown error"}.` });
    }
  }

  function clearScreenplay() {
    setScreenplay(null);
    setScriptStatus("idle");
    setScriptEngine("");
    setScriptMessage("Screenplay cleared. Renders fall back to template beats until you write a new draft.");
    setReferenceRegistry(createEmptyReferenceRegistry("Screenplay cleared. Plan references again for the template setup."));
    markRenderPackageStale("Screenplay cleared. Build the render package again before sending.");
  }

  function patch(changes: Partial<StoryState>) {
    setState((current) => ({ ...current, ...changes }));
    setReferenceRegistry((current) => current.status === "empty"
      ? current
      : createEmptyReferenceRegistry("Settings changed. Plan and generate references again for this story setup.")
    );
    setHandoff((current) => current.packageBuiltAt
      ? {
          ...current,
          packageBuiltAt: null,
          estimateReviewedAt: null,
          estimateCredits: null,
          status: "idle",
          jobId: "",
          message: "Settings changed. Rebuild the render package before sending."
        }
      : current
    );
  }

  function selectEvolinkModel(evolinkModel: string) {
    patch({
      evolinkModel,
      videoResolution: normalizeVideoResolution(evolinkModel, state.videoResolution)
    });
  }

  function loadSample() {
    setState({
      ...defaultState,
      seed: "A disgraced wedding singer gets 24 hours to reunite a feuding alien family before their intergalactic divorce destroys Las Vegas.",
      format: "trailer",
      aspect: "16x9",
      genre: "comedy",
      tone: "funny",
      audience: "streaming",
      structure: "save_cat",
      arc: "underdog",
      stakes: "public_spectacle",
      twist: "false_ally",
      era: "modern",
      visual: "bright_comedy",
      director: "bright_ensemble",
      runtime: 60
    });
    setGeneratedAt(null);
    setRenderGates(defaultRenderGates);
    setApprovedCapCredits(DEFAULT_RENDER_CAP);
    setHandoff({
      packageBuiltAt: null,
      estimateReviewedAt: null,
      estimateCredits: null,
      status: "idle",
      message: "",
      jobId: ""
    });
    setReferenceRegistry(createEmptyReferenceRegistry());
    setScreenplay(null);
    setScriptStatus("idle");
    setScriptEngine("");
    setScriptMessage("");
    setScriptNote("");
  }

  function generateBlueprint() {
    setGeneratedAt(currentTimeLabel());
    setHandoff((current) => ({
      ...current,
      status: current.packageBuiltAt ? current.status : "idle",
      message: current.packageBuiltAt ? current.message : "Blueprint created. Build the EvoLink render package when ready."
    }));
  }

  function markRenderPackageStale(message: string) {
    setHandoff((current) => current.packageBuiltAt
      ? {
          ...current,
          packageBuiltAt: null,
          estimateReviewedAt: null,
          estimateCredits: null,
          status: "idle",
          jobId: "",
          message
        }
      : {
          ...current,
          message
        }
    );
  }

  function planReferences() {
    const planned = planReferenceRegistry(state, blueprint, screenplay);
    setReferenceRegistry(planned);
    markRenderPackageStale("Reference plan updated. Build the render package again before live send.");
  }

  function registryWithStatus(registry: ReferenceRegistry, status: ReferenceAssetStatus, message: string): ReferenceRegistry {
    return {
      ...registry,
      characterCards: registry.characterCards.map((asset) => ({ ...asset, status })),
      sceneReferences: registry.sceneReferences.map((asset) => ({ ...asset, status })),
      status: status === "generating" ? "generating" : registry.status,
      message,
      updatedAt: currentTimeLabel()
    };
  }

  function mergeGeneratedCards(plannedCards: ReferenceAsset[], result: Record<string, unknown>): ReferenceAsset[] {
    const cards = recordArray(result.cards);
    const fallbackStatus: ReferenceAssetStatus = textValue(result.status) === "not_configured" ? "not_configured" : "missing";
    return plannedCards.map((asset) => {
      const match = cards.find((card) => textValue(card.characterId) === asset.id || textValue(card.name) === asset.characterName);
      const storageKey = textValue(match?.storageKey);
      const sourceUrl = textValue(match?.sourceUrl || match?.imageUrl);
      return {
        ...asset,
        fileName: textValue(match?.fileName) || asset.fileName,
        storageKey,
        sourceUrl,
        status: storageKey ? "generated" : fallbackStatus,
        provider: storageKey ? "openai" : asset.provider,
        sourceMode: storageKey ? "chatgpt_image" : asset.sourceMode
      };
    });
  }

  function mergeGeneratedSceneReferences(plannedScenes: ReferenceAsset[], result: Record<string, unknown>): ReferenceAsset[] {
    const plates = recordArray(result.plates);
    const fallbackStatus: ReferenceAssetStatus = textValue(result.status) === "not_configured" ? "not_configured" : "missing";
    return plannedScenes.map((asset) => {
      const match = plates.find((plate) => textValue(plate.sceneId) === asset.sceneId || textValue(plate.sceneLabel) === asset.sceneLabel);
      const storageKey = textValue(match?.storageKey);
      const sourceUrl = textValue(match?.sourceUrl || match?.imageUrl);
      return {
        ...asset,
        label: textValue(match?.sceneLabel) || asset.label,
        fileName: textValue(match?.fileName) || asset.fileName,
        storageKey,
        sourceUrl,
        status: storageKey ? "generated" : fallbackStatus,
        provider: storageKey ? "openai" : asset.provider,
        sourceMode: storageKey ? "chatgpt_image" : asset.sourceMode
      };
    });
  }

  function updateReferenceAsset(assetId: string, changes: Partial<ReferenceAsset>, message: string) {
    setReferenceRegistry((current) => {
      const update = (asset: ReferenceAsset): ReferenceAsset => asset.id === assetId ? { ...asset, ...changes } : asset;
      return finalizeReferenceRegistry({
        ...current,
        characterCards: current.characterCards.map(update),
        sceneReferences: current.sceneReferences.map(update),
        message,
        updatedAt: currentTimeLabel()
      });
    });
  }

  async function uploadReferenceAsset(asset: ReferenceAsset, file: File) {
    updateReferenceAsset(asset.id, {
      status: "generating",
      fileName: file.name,
      provider: "user",
      sourceMode: "user_upload"
    }, `Uploading ${asset.label}.`);

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("projectId", safeProjectId(blueprint.title));
      formData.append("kind", asset.kind);
      formData.append("label", asset.label);
      formData.append("role", asset.role || "");
      formData.append("sceneId", asset.sceneId || "");
      formData.append("sceneLabel", asset.sceneLabel || "");
      formData.append("characterName", asset.characterName || "");
      formData.append("sourceMode", "user_upload");
      formData.append("provider", "user");
      formData.append("sourceOfTruth", "true");
      formData.append("locked", "true");

      const result = await apiFormPost<UploadedAssetResponse>("/api/assets/upload", formData);
      const uploaded = result.asset || {};
      const storageKey = textValue(uploaded.storageKey);
      if (!storageKey) {
        throw new Error(result.message || "Upload did not return a reusable storage key.");
      }
      updateReferenceAsset(asset.id, {
        storageKey,
        sourceUrl: textValue(uploaded.sourceUrl),
        fileName: textValue(uploaded.fileName) || file.name,
        status: "uploaded",
        provider: "user",
        sourceMode: "user_upload",
        locked: true,
        sourceOfTruth: true
      }, `${asset.label} uploaded and ready for EvoLink imageKeys.`);
      markRenderPackageStale("Reference uploads changed. Build the render package again so the latest imageKeys are reviewed.");
    } catch (error) {
      updateReferenceAsset(asset.id, {
        status: "failed",
        provider: "user",
        sourceMode: "user_upload"
      }, `Upload failed for ${asset.label}: ${error instanceof Error ? error.message : "unknown error"}.`);
    }
  }

  async function generateReferences() {
    if (!referencesRequired) return;
    const planned = referenceCounts.total ? referenceRegistry : planReferenceRegistry(state, blueprint, screenplay);
    const projectId = safeProjectId(blueprint.title);
    const style = [
      findOption(visuals, state.visual).label,
      findOption(eras, state.era).label,
      findOption(directors, state.director).label,
      findOption(characterLooks, state.characterLook).label
    ].join(", ");
    const generatingRegistry = registryWithStatus(planned, "generating", "Generating reference assets with ChatGPT Image 2.");
    setReferenceRegistry(generatingRegistry);

    let nextCards = planned.characterCards;
    let nextScenes = planned.sceneReferences;
    const warnings: string[] = [];

    try {
      if (planned.characterCards.length) {
        const result = await apiPost<Record<string, unknown>>("/api/character-cards", {
          projectId,
          settings: {
            projectId,
            style,
            imageQuality: "low",
            imageSize: "1024x1024"
          },
          characters: buildCharacterSpecs(state, blueprint, screenplay)
        });
        nextCards = mergeGeneratedCards(planned.characterCards, result);
        warnings.push(...recordArray(result.warnings).map((warning) => JSON.stringify(warning)));
        if (Array.isArray(result.warnings)) {
          warnings.push(...result.warnings.map((warning) => textValue(warning)).filter(Boolean));
        }
      }
    } catch (error) {
      nextCards = planned.characterCards.map((asset) => ({ ...asset, status: "failed" as const }));
      warnings.push(`Character cards: ${error instanceof Error ? error.message : "unknown error"}.`);
    }

    try {
      if (planned.sceneReferences.length) {
        const result = await apiPost<Record<string, unknown>>("/api/scene-references", {
          projectId,
          settings: {
            projectId,
            style,
            imageQuality: "low",
            imageSize: imageSizeForAspect(state.aspect)
          },
          scenes: buildSceneSpecs(state, blueprint, screenplay)
        });
        nextScenes = mergeGeneratedSceneReferences(planned.sceneReferences, result);
        if (Array.isArray(result.warnings)) {
          warnings.push(...result.warnings.map((warning) => textValue(warning)).filter(Boolean));
        }
      }
    } catch (error) {
      nextScenes = planned.sceneReferences.map((asset) => ({ ...asset, status: "failed" as const }));
      warnings.push(`Scene plates: ${error instanceof Error ? error.message : "unknown error"}.`);
    }

    const finalized = finalizeReferenceRegistry({
      ...planned,
      characterCards: nextCards,
      sceneReferences: nextScenes
    }, warnings.filter(Boolean));
    setReferenceRegistry(finalized);
    markRenderPackageStale("Reference assets changed. Build the render package again so the latest imageKeys are reviewed.");
  }

  function buildPackage() {
    if (!generatedAt) {
      generateBlueprint();
    }
    const registryForPackage = referencesRequired && referenceCounts.total === 0
      ? planReferenceRegistry(state, blueprint, screenplay)
      : referenceRegistry;
    if (registryForPackage !== referenceRegistry) {
      setReferenceRegistry(registryForPackage);
    }
    const packagedCounts = countReferenceRegistry(registryForPackage);
    const packagedReferencesReady = !referencesRequired || (packagedCounts.total > 0 && packagedCounts.ready === packagedCounts.total);
    setActive("Review");
    setHandoff({
      packageBuiltAt: currentTimeLabel(),
      estimateReviewedAt: null,
      estimateCredits: null,
      status: "built",
      message: packagedReferencesReady
        ? "Dry-run render package prepared for backend review."
        : "Dry-run render package prepared. Generate or connect the planned references before live send.",
      jobId: ""
    });
  }

  async function reviewEstimate() {
    if (!generatedAt || !handoff.packageBuiltAt) return;
    const localEstimate = renderPlan.estimatedCredits;
    setHandoff((current) => ({
      ...current,
      status: "estimating",
      message: "Checking the backend render estimate."
    }));

    try {
      const payload = buildRenderPayload(state, blueprint, renderPlan, renderGates, approvedCapCredits, referenceRegistry, screenplay);
      const result = await apiPost<{ estimatedCredits?: number; status?: string }>("/api/render/estimate", payload);
      setHandoff((current) => ({
        ...current,
        estimateReviewedAt: currentTimeLabel(),
        estimateCredits: typeof result.estimatedCredits === "number" ? result.estimatedCredits : localEstimate,
        status: "estimated",
        message: "Backend estimate reviewed. Approval gates can unlock the live send."
      }));
    } catch (error) {
      setHandoff((current) => ({
        ...current,
        estimateReviewedAt: currentTimeLabel(),
        estimateCredits: localEstimate,
        status: "estimated",
        message: `Local estimate reviewed. Backend estimate unavailable: ${error instanceof Error ? error.message : "unknown error"}.`
      }));
    }
  }

  async function sendToEvolink() {
    if (!sendReady) return;
    setHandoff((current) => ({
      ...current,
      status: "sending",
      message: "Sending the approved package to the backend render gate."
    }));

    try {
      const payload = buildRenderPayload(state, blueprint, renderPlan, renderGates, approvedCapCredits, referenceRegistry, screenplay);
      const result = await apiPost<{ ok: boolean; status: string; message?: string; job?: { id?: string } }>("/api/render/start", payload);
      if (result.ok) {
        setRenderJob(null);
        setQcReports({});
        setStitch(idleStitchState);
      }
      setHandoff((current) => ({
        ...current,
        status: result.ok ? "sent" : "error",
        jobId: result.job?.id || "",
        message: result.ok
          ? `EvoLink render job started: ${result.job?.id || "job pending"}.`
          : result.message || `Render gate returned ${result.status}.`
      }));
    } catch (error) {
      setHandoff((current) => ({
        ...current,
        status: "error",
        message: `Send blocked: ${error instanceof Error ? error.message : "unknown error"}.`
      }));
    }
  }

  function updateGate(key: RenderGateKey, checked: boolean) {
    setRenderGates((current) => ({ ...current, [key]: checked }));
  }

  function primaryActionLabel(): string {
    if (!generatedAt) return "Generate Story Blueprint";
    if (!handoff.packageBuiltAt) return "Build EvoLink Package";
    if (!handoff.estimateReviewedAt) return "Review Cost Estimate";
    if (handoff.status === "sent") return "Render Job Sent";
    if (sendReady) return "Send to EvoLink";
    return "Open Review Gate";
  }

  function handlePrimaryAction() {
    if (!generatedAt) {
      generateBlueprint();
      return;
    }
    if (!handoff.packageBuiltAt) {
      buildPackage();
      return;
    }
    if (!handoff.estimateReviewedAt) {
      void reviewEstimate();
      return;
    }
    if (sendReady) {
      void sendToEvolink();
      return;
    }
    setActive("Review");
  }

  return (
    <div className="app">
      <Sidebar active={active} onActive={setActive} />
      <main className="workspace">
        <header className="topbar">
          <div>
            <span>Kinetix Film Pipeline</span>
            <h1>Create an engaging video story</h1>
          </div>
          <div className="topbar-actions">
            <button onClick={loadSample} type="button">Try Sample</button>
            <button type="button">Save Blueprint</button>
          </div>
        </header>

        {generatedAt ? (
          <div className="run-banner" role="status">
            <Icon name="spark" />
            <div>
              <strong>Pipeline draft created at {generatedAt}</strong>
              <span>{blueprint.sceneCount} scenes, {blueprint.chunkCount} video chunks, character cards, storyboards, and {findOption(evolinkModels, state.evolinkModel).label} at {resolutionLabel(normalizeVideoResolution(state.evolinkModel, state.videoResolution))} are ready for the Kinetix Film pipeline.</span>
            </div>
          </div>
        ) : null}

        <div className="stepper" aria-label="Creation steps">
          <span className="active">1 Choose Story</span>
          <span>2 Shape Visuals</span>
          <span>3 Review Package</span>
          <span>4 Send to EvoLink</span>
        </div>

        <div className="content-grid">
          <div className="builder">
            <PreviousProjectSetup state={state} patch={patch} />

            <Section title="Story Seed" action={<span className="soft-count">{state.seed.length} characters</span>}>
              <div className="seed-box">
                <textarea
                  aria-label="Story seed"
                  value={state.seed}
                  onChange={(event) => patch({ seed: event.target.value })}
                />
                <div className="seed-actions">
                  <button className="mini-action" type="button">
                    <Icon name="spark" />
                    Enhance with ChatGPT Image 2
                  </button>
                  <button className="mini-action" onClick={() => patch({ seed: "" })} type="button">Clear</button>
                </div>
              </div>
            </Section>

            <Section title="Quick Story Choices">
              <OptionGrid options={formats} selected={state.format} onSelect={(format) => patch(formatSelectionPatch(format, state.runtime))} variant="compact" />
              <div className="quick-row">
                <SegmentGroup options={aspects} selected={state.aspect} onSelect={(aspect) => patch({ aspect })} label="Aspect Ratio" />
                <SegmentGroup
                  options={runtimeOptions}
                  selected={String(state.runtime)}
                  onSelect={(runtime) => patch({ runtime: Number(runtime) })}
                  label="Length"
                />
              </div>
              <label className="custom-runtime-control">
                <span>Custom seconds</span>
                <input
                  aria-label="Custom runtime seconds"
                  min={1}
                  onChange={(event) => patch({ runtime: clampRuntimeSeconds(Number(event.target.value), state.runtime) })}
                  step={1}
                  type="number"
                  value={state.runtime}
                />
              </label>
            </Section>

            <Section title="Genre">
              <OptionGrid options={genres} selected={state.genre} onSelect={(genre) => patch({ genre, subGenre: "" })} />
              {(genreFlavors[state.genre] || []).length ? (
                <SegmentGroup
                  label="Flavor"
                  options={[{ id: "", label: "Classic", detail: "The genre's core voice." }, ...(genreFlavors[state.genre] || [])]}
                  selected={state.subGenre}
                  onSelect={(subGenre) => patch({ subGenre })}
                />
              ) : null}
            </Section>

            <div className="two-column">
              <Section title="Audience & Tone">
                <OptionGrid options={audiences} selected={state.audience} onSelect={(audience) => patch({ audience })} variant="compact" />
                <SegmentGroup options={tones} selected={state.tone} onSelect={(tone) => patch({ tone })} label="Tone" />
              </Section>

              <Section title="Character Engine">
                <div className="range-control">
                  <div>
                    <strong>Character Development Depth</strong>
                    <small>{state.characterDepth}/5 · {state.characterDepth >= 4 ? "psychological arc and world ties" : "fast readable motivations"}</small>
                  </div>
                  <input
                    min="1"
                    max="5"
                    type="range"
                    value={state.characterDepth}
                    onChange={(event) => patch({ characterDepth: Number(event.target.value) })}
                  />
                </div>
                <div className="range-control">
                  <div>
                    <strong>Cast Size</strong>
                    <small>{state.castSize} key characters</small>
                  </div>
                  <input
                    min="1"
                    max="5"
                    type="range"
                    value={state.castSize}
                    onChange={(event) => patch({ castSize: Number(event.target.value) })}
                  />
                </div>
                <OptionGrid options={arcs} selected={state.arc} onSelect={(arc) => patch({ arc })} variant="compact" />
              </Section>
            </div>

            <CharacterCreationStudio state={state} patch={patch} />

            <ScreenplayPanel
              engine={scriptEngine}
              message={scriptMessage}
              note={scriptNote}
              onClear={clearScreenplay}
              onGenerate={() => void requestScreenplay("generate")}
              onNoteChange={setScriptNote}
              onProviderChange={(scriptProvider) => patch({ scriptProvider })}
              onRefine={() => void requestScreenplay("refine")}
              onSpokenDialogueChange={(spokenDialogue) => patch({ spokenDialogue })}
              onImport={importScreenplay}
              onCoverage={() => void getCoverage()}
              coverage={coverage}
              onPunchUp={() => void requestScreenplay("punch_up")}
              onVary={(warning) => void requestScreenplay("refine", varyShotNote(warning))}
              screenplay={screenplay}
              stale={screenplayIsStale(state, screenplay)}
              state={state}
              status={scriptStatus}
            />

            <ReferenceAssetPanel
              counts={referenceCounts}
              onGenerate={() => void generateReferences()}
              onPlan={planReferences}
              onUpload={(asset, file) => void uploadReferenceAsset(asset, file)}
              referencesReady={referencesReady}
              referencesRequired={referencesRequired}
              registry={referenceRegistry}
              state={state}
            />

            <Section title="Story Arc">
              <div className="three-lane">
                <div>
                  <h3>Structure</h3>
                  <OptionGrid options={structures} selected={state.structure} onSelect={(structure) => patch({ structure })} variant="compact" />
                </div>
                <div>
                  <h3>Stakes</h3>
                  <OptionGrid options={stakes} selected={state.stakes} onSelect={(value) => patch({ stakes: value })} variant="compact" />
                </div>
                <div>
                  <h3>Twist</h3>
                  <OptionGrid options={twists} selected={state.twist} onSelect={(twist) => patch({ twist })} variant="compact" />
                </div>
              </div>
            </Section>

            <StoryLogicPanel blueprint={blueprint} />

            <Section title="Visual DNA">
              <OptionGrid options={visuals} selected={state.visual} onSelect={(visual) => patch({ visual })} variant="visual" />
              <div className="quick-row">
                <SegmentGroup options={eras} selected={state.era} onSelect={(era) => patch({ era })} label="Era" />
                <SegmentGroup options={directors} selected={state.director} onSelect={(director) => patch({ director })} label="Director Language" />
              </div>
            </Section>

            <Section title="Pipeline Output">
              <div className="pipeline-model-row">
                <SegmentGroup
                  options={evolinkModels}
                  selected={state.evolinkModel}
                  onSelect={selectEvolinkModel}
                  label="EvoLink Video Model"
                />
                <SegmentGroup
                  options={availableVideoResolutions(state.evolinkModel)}
                  selected={normalizeVideoResolution(state.evolinkModel, state.videoResolution)}
                  onSelect={(videoResolution) => patch({ videoResolution })}
                  label="Generation Resolution"
                />
                <ToggleRow
                  checked={state.autoUpscale}
                  detail={`Final delivery ${state.autoUpscale ? `auto upscales to ${upscaleTargetForResolution(normalizeVideoResolution(state.evolinkModel, state.videoResolution))}` : "stays at generation resolution"}.`}
                  label="Auto Upscale"
                  onChange={(autoUpscale) => patch({ autoUpscale })}
                />
              </div>

              <div className="chunk-control-panel">
                <SegmentGroup
                  options={chunkProfiles}
                  selected={state.chunkProfile}
                  onSelect={(chunkProfile) => patch({ chunkProfile })}
                  label="Clip Strategy"
                />
                <ToggleRow
                  checked={state.continuityChain}
                  detail="Render clips one at a time, feeding each finished clip into the next as a Seedance video reference — adjacent clips share real geography instead of teleporting. Slower, same cost."
                  label="Continuity Chain"
                  onChange={(continuityChain) => patch({ continuityChain })}
                />
                <div className="guardrail-strip" aria-label="EvoLink continuity guardrails">
                  <span>
                    <b>{targetChunkSeconds(state)}s</b>
                    Target clip length
                  </span>
                  <span>
                    <b>{blueprint.chunkCount}</b>
                    Planned clips
                  </span>
                  <span>
                    <b>20s</b>
                    Max total video refs per long beat
                  </span>
                </div>
                <p className="audio-note">Use 15s continuity when repeated motion appears; use one immediate prior video reference for the next beat and keep added video refs short.</p>
              </div>

              <div className="audio-control-panel">
                <SegmentGroup
                  options={audioModes}
                  selected={state.audioMode}
                  onSelect={(audioMode) => {
                    const lanePatch =
                      audioMode === "character_lines"
                        ? { audioSfx: true, audioDialogue: true, audioVoiceover: false, audioScore: true, audioMusicBed: false }
                        : audioMode === "natural_foley"
                          ? { audioSfx: true, audioDialogue: false, audioVoiceover: false, audioScore: false, audioMusicBed: false }
                          : audioMode === "trailer_mix"
                            ? { audioSfx: true, audioDialogue: true, audioVoiceover: true, audioScore: true, audioMusicBed: true }
                            : {};
                    patch({ audioMode, voicePlan: audioMode !== "silent_planning", ...lanePatch });
                  }}
                  label="Audio Mode"
                />
                <div className="toggle-grid tight">
                  <ToggleRow
                    checked={state.voicePlan}
                    detail="Enable Seedance-generated sound lanes for rendered clips."
                    label="Generate Audio"
                    onChange={(voicePlan) => patch({
                      voicePlan,
                      audioMode: voicePlan && state.audioMode === "silent_planning" ? "story_mix" : state.audioMode
                    })}
                  />
                  <ToggleRow
                    checked={state.audioSfx}
                    detail="Footsteps, breathing, props, weather, rooms, doors, and action sounds."
                    label="Natural SFX"
                    onChange={(audioSfx) => patch({ audioSfx })}
                  />
                  <ToggleRow
                    checked={state.seedAudioVoices}
                    detail="Generate each spoken line with Seed Audio 1.0 first, then lip-sync the clip to it (@audio1). Off = Seedance's own voices (default)."
                    label="Seed Audio 1.0 Voices"
                    onChange={(seedAudioVoices) => patch({ seedAudioVoices })}
                  />
                  <ToggleRow
                    checked={state.audioDialogue}
                    detail="Allow scripted character speech when a beat includes approved lines."
                    label="Dialogue"
                    onChange={(audioDialogue) => patch({ audioDialogue })}
                  />
                  <ToggleRow
                    checked={state.audioVoiceover}
                    detail="Optional narrator or trailer VO when the story calls for it."
                    label="Narrator / VO"
                    onChange={(audioVoiceover) => patch({ audioVoiceover })}
                  />
                  <ToggleRow
                    checked={state.audioScore}
                    detail="Allow cinematic score, stings, risers, and impact hits."
                    label="Score"
                    onChange={(audioScore) => patch({ audioScore })}
                  />
                  <ToggleRow
                    checked={state.audioMusicBed}
                    detail="Allow a restrained music bed that supports pacing and emotion."
                    label="Music Bed"
                    onChange={(audioMusicBed) => patch({ audioMusicBed })}
                  />
                </div>
                <p className="audio-note">White-noise, pink-noise, and constant hiss washes stay out by default; dialogue, score, music, and VO stay available when the story calls for them.</p>
              </div>

              <div className="toggle-grid">
                <ToggleRow
                  checked={state.imageModel}
                  detail="Character portraits, key visuals, and scene concepts."
                  label="ChatGPT Image 2 Assets"
                  onChange={(imageModel) => patch({ imageModel })}
                />
                <ToggleRow
                  checked={state.storyboards}
                  detail="Storyboard boards and shot prompts for the video engine."
                  label="Storyboard Plan"
                  onChange={(storyboards) => patch({ storyboards })}
                />
                <ToggleRow
                  checked={state.voicePlan}
                  detail={audioModeSummary(state)}
                  label="Audio & Voice Plan"
                  onChange={(voicePlan) => patch({
                    voicePlan,
                    audioMode: voicePlan && state.audioMode === "silent_planning" ? "story_mix" : state.audioMode
                  })}
                />
                <ToggleRow
                  checked={state.pitchDeck}
                  detail="Optional deck framing for collaborators or buyers."
                  label="Pitch Deck"
                  onChange={(pitchDeck) => patch({ pitchDeck })}
                />
                <ToggleRow
                  checked={state.renderPackage}
                  detail="Pipeline-ready scene chunks, manifests, and delivery files."
                  label="Render Package"
                  onChange={(renderPackage) => patch({ renderPackage })}
                />
              </div>
            </Section>

            <EvolinkSendPanel
              approvedCapCredits={approvedCapCredits}
              blueprint={blueprint}
              gates={renderGates}
              generatedAt={generatedAt}
              handoff={handoff}
              onBuildPackage={buildPackage}
              onCapChange={setApprovedCapCredits}
              onGateChange={updateGate}
              onReviewEstimate={() => void reviewEstimate()}
              onSend={() => void sendToEvolink()}
              referenceCounts={referenceCounts}
              referencesReady={referencesReady}
              referencesRequired={referencesRequired}
              renderPlan={renderPlan}
              state={state}
            />

            <RenderJobPanel
              job={renderJob}
              jobError={renderJobError}
              onQc={(task) => void runClipQc(task)}
              onRefresh={() => void refreshRenderJob()}
              onRetry={(task) => void retryClip(task)}
              onStitch={() => void startStitch()}
              qcReports={qcReports}
              retryBusy={retryBusy}
              stitch={stitch}
            />
          </div>

          <BlueprintPanel blueprint={blueprint} referenceCounts={referenceCounts} renderPlan={renderPlan} sendReady={sendReady} />
        </div>
      </main>

      <div className="create-bar">
        <div>
          <strong>{generatedAt ? "Story Blueprint Ready" : "Create Story Film"}</strong>
          <span>{blueprint.outputShape}</span>
        </div>
        <button disabled={handoff.status === "sending"} onClick={handlePrimaryAction} type="button">
          <Icon name="play" />
          {handoff.status === "sending" ? "Sending to EvoLink" : primaryActionLabel()}
        </button>
      </div>
    </div>
  );
}
